/**
 * Gemini Service - Video/Media Analysis API Integration
 * SLAM Sports AI - College Recruiter Analysis System
 *
 * Routes all Gemini API calls through backend proxy to avoid HTTP referrer restrictions.
 * Supports multiple analysis modes with Mixture of Experts (MoE) prompts.
 *
 * Architecture:
 * - Small files (< 32MB): Upload via backend proxy → Gemini File API
 * - Large files (> 32MB): Direct upload to GCS → Gemini reads gs:// URI
 * - All generateContent calls: Backend proxy (avoids API key referrer restrictions)
 */

import { MediaType } from './types';
import { generateMoESystemPrompt, EXPERT_PERSONAS } from './expertPersonas';
import { generateHitterMoESystemPrompt } from './hitterExpertPersonas';
// REMOVED: Basketball MoE - was overriding user prompts with templated analysis
// import { generateBasketballMoESystemPrompt } from './basketballExpertPersonas';

/**
 * Simple Mechanics Analysis Prompt
 * Fast, focused single-pass analysis using gemini-2.5-flash
 */
const SIMPLE_MECHANICS_PROMPT = `You are SLAM Sports AI, an expert pitching mechanics analyst.

Provide a SIMPLE, FOCUSED mechanical breakdown of this pitcher. Keep it concise and actionable.
**CRITICAL: Reference specific video timestamps (MM:SS format) for every observation so coaches can review the exact moments in film.**

## OUTPUT FORMAT

### Delivery Overview
Brief description of the pitcher's delivery style (over-the-top, 3/4, sidearm, etc.) and overall tempo.

### Key Mechanics (with timestamps)
1. **Windup/Stretch** [MM:SS]: [1-2 sentences on setup position]
2. **Leg Lift** [MM:SS]: [1-2 sentences on balance and timing]
3. **Hip-Shoulder Separation** [MM:SS]: [1-2 sentences on torque generation]
4. **Arm Action** [MM:SS]: [1-2 sentences on arm path and slot]
5. **Release Point** [MM:SS]: [1-2 sentences on consistency and extension]
6. **Follow Through** [MM:SS]: [1-2 sentences on finish and deceleration]

### Strengths (Top 2)
- [Specific strength with brief explanation] — see [MM:SS]
- [Specific strength with brief explanation] — see [MM:SS]

### Areas to Watch (Top 2)
- [Specific concern with brief explanation] — see [MM:SS]
- [Specific concern with brief explanation] — see [MM:SS]

### Quick Take
One paragraph summary: What makes this pitcher effective or vulnerable? What's the main thing to know?

---
Keep it simple. Keep it focused. No fluff. Always cite timestamps.`;

/**
 * Expert Drill-Down Prompts
 * Detailed prompts for deep-diving into specific expert analysis areas
 */
const EXPERT_DRILLDOWN_PROMPTS = {
  BIOMECHANICS_EXPERT: `You are SLAM Sports AI - BIOMECHANICS SPECIALIST.

Provide an IN-DEPTH biomechanical analysis of this pitcher. Focus exclusively on kinematic and motion analysis.
**CRITICAL: Every observation MUST include a video timestamp (MM:SS) so coaches can review the exact frame. This is non-negotiable.**

## DETAILED BIOMECHANICAL BREAKDOWN

### Joint Angles & Positions
For each, cite the timestamp where observed:
- Shoulder external rotation at maximum — [MM:SS]
- Elbow angle at foot strike and release — [MM:SS]
- Hip-knee-ankle alignment throughout delivery — [MM:SS]
- Trunk tilt (forward and lateral) measurements — [MM:SS]
- Lead leg block angle and stability — [MM:SS]

### Timing & Sequencing
- Stride length relative to height — [MM:SS]
- Hip-to-shoulder separation timing — [MM:SS]
- Arm lag and acceleration phase — [MM:SS]
- Release point consistency frame-by-frame — [MM:SS to MM:SS]
- Deceleration mechanics post-release — [MM:SS]

### Kinetic Chain Analysis
- Energy transfer efficiency through lower body — [MM:SS]
- Core engagement and stabilization — [MM:SS]
- Scapular loading and movement — [MM:SS]
- Wrist and finger position at release — [MM:SS]

### Variance Analysis
- Compare mechanics between pitch types (if visible) with timestamps for each pitch
- Note any inconsistencies in delivery — [MM:SS vs MM:SS]
- Quantify angle differences where detectable

### Injury Risk Assessment
- Movement patterns that may increase stress — see [MM:SS]
- Areas of compensation or inefficiency — see [MM:SS]
- Recommendations for mechanical optimization with reference footage timestamps

### Video Evidence Summary
| Observation | Timestamp | Confidence |
|-------------|-----------|------------|
| [Key finding] | [MM:SS] | HIGH/MEDIUM/LOW |
(Include at least 5 rows)

### Confidence Level
Rate your confidence in each observation (HIGH/MEDIUM/LOW) based on video quality and visibility.`,

  VISUAL_ANALYSIS_EXPERT: `You are SLAM Sports AI - VISUAL ANALYSIS SPECIALIST.

Provide an IN-DEPTH visual breakdown of this pitcher. Focus on observable cues and patterns.
**CRITICAL: Every observation MUST include a specific video timestamp (MM:SS). Coaches will use these timestamps to review film. This is the most important part of the output.**

## DETAILED VISUAL ANALYSIS

### Glove Position Tracking (with timestamps)
- Set position height (relative to letters, chest, belt) — [MM:SS]
- Glove width from body at set — [MM:SS]
- Movement patterns during delivery — [MM:SS to MM:SS]
- Glove position at leg lift peak — [MM:SS]
- Final glove tuck location — [MM:SS]

### Grip Visibility Assessment
- Can grip be seen in the glove? — [MM:SS]
- Any re-gripping motions detected? — [MM:SS]
- Hand movement in glove before separation — [MM:SS]
- Grip exposure at release — [MM:SS]

### Pre-Release Cues
- Head position and tracking — [MM:SS]
- Eye focus direction — [MM:SS]
- Body rhythm and tempo — [MM:SS]
- Timing variations between pitches — [MM:SS vs MM:SS]

### Pitch-by-Pitch Comparison
For each pitch visible, provide a timestamp-referenced comparison:
| Pitch # | Timestamp | Pitch Type (if known) | Key Visual Differences | Tell Detected? |
|---------|-----------|----------------------|----------------------|----------------|
| 1 | [MM:SS] | [type] | [differences] | Yes/No |
(Include ALL pitches visible in the video)

### Exploitation Potential
For each exploitable tell:
1. **Tell**: [Description] — First visible at [MM:SS], also at [MM:SS, MM:SS]
2. **Detection Window**: How early before release the tell appears
3. **Reliability**: [% estimate based on sample in video]
4. **Hitter Action**: What to do when this tell is spotted

### Video Evidence Summary
| Tell/Observation | Timestamps (all occurrences) | Exploitability (1-10) |
|-----------------|------------------------------|----------------------|
| [Finding] | [MM:SS, MM:SS, MM:SS] | [Score] |
(Minimum 5 rows linking observations to exact video moments)`,

  EXPLOITATION_STRATEGY_EXPERT: `You are SLAM Sports AI - EXPLOITATION STRATEGY SPECIALIST.

Provide an IN-DEPTH exploitation game plan based on this pitcher's tendencies. Focus on OFFENSIVE advantage.
**CRITICAL: Link every exploitation opportunity to specific video timestamps (MM:SS) so hitters can SEE what you're describing. Show, don't just tell.**

## DETAILED EXPLOITATION STRATEGY

### Primary Exploitation Opportunities
For each detected tell:
1. **Tell Description**: [What to look for]
2. **Video Evidence**: [MM:SS, MM:SS, MM:SS] — timestamps showing this tell
3. **When It Appears**: [Set position, leg lift, etc.] — detection window in delivery
4. **What It Reveals**: [Pitch type prediction]
5. **Reliability**: [% estimate based on occurrences in video]
6. **Hitter Action**: [Specific approach when detected]
7. **Pre-Game Communication**: "Watch for [tell] at [phase]. When you see it, [action]."

### Count-Specific Game Plans

#### Hitter's Counts (1-0, 2-0, 2-1, 3-1)
- Primary pitch to anticipate — evidence at [MM:SS]
- Best approach when tell is detected
- Zone selection adjustment

#### Even Counts (0-0, 1-1, 2-2)
- Balance between sitting and reacting
- Which tells are most reliable here — see [MM:SS]

#### Pitcher's Counts (0-1, 0-2, 1-2)
- Protective approach adjustments
- What to look for with 2 strikes — see [MM:SS]

### Situational Exploitation
- RISP approach modifications
- First at-bat vs later at-bats
- How tells may change with runners on

### Pre-Game Scouting Brief
One paragraph that could be read to hitters before the game. Include specific timestamps for the video review session: "Pull up [MM:SS] to show the glove tell, then [MM:SS] for the timing difference."

### Video Review Sequence
List the top 5 timestamps in order for a pre-game video session with hitters:
1. [MM:SS] — Shows [key tell/observation]
2. [MM:SS] — Shows [key tell/observation]
3. [MM:SS] — Shows [key tell/observation]
4. [MM:SS] — Shows [key tell/observation]
5. [MM:SS] — Shows [key tell/observation]

### Success Probability
Estimated batting average advantage when tells are successfully exploited.`,

  GAME_DYNAMICS_EXPERT: `You are SLAM Sports AI - GAME DYNAMICS SPECIALIST.

Provide an IN-DEPTH analysis of how this pitcher's tendencies play out in live game situations.
**CRITICAL: Reference specific video timestamps (MM:SS) for every observation. Coaches need to pull up exact moments during film sessions.**

## DETAILED GAME DYNAMICS ANALYSIS

### In-Game Detection Protocol
- What to watch for from the dugout — example at [MM:SS]
- Optimal viewing angle recommendations
- Communication protocol for detected tells

### Fatigue Indicators
- Signs that pitcher is tiring — visible at [MM:SS] vs earlier at [MM:SS]
- How mechanics change with fatigue — compare [MM:SS] (fresh) to [MM:SS] (fatigued)
- When tells become more pronounced — [MM:SS]

### Pressure Situations
- Expected changes in high-leverage moments — see [MM:SS]
- Tendencies with runners in scoring position
- How delivery changes with different leads

### Hitter Timing Adjustments
- Recommended load timing modifications — reference delivery at [MM:SS]
- Decision point optimization
- When to commit vs when to react

### Run Value Impact
- Estimated runs saved/created per game from exploitation
- Which situations have highest leverage
- Expected wOBA improvement when tells are used

### Counter-Scout Assessment
- How might the pitcher adjust if they notice?
- Signs that tells have been corrected
- Backup exploitation strategies

### Key Film Review Timestamps
| Timestamp | What to Observe | Coaching Takeaway |
|-----------|----------------|-------------------|
| [MM:SS] | [Observation] | [Action item] |
(Include at least 5 key moments for the coaching staff film session)`,

  BROADCAST_EXPERT: `You are SLAM Sports AI - BROADCAST ANALYSIS SPECIALIST.

Provide a clear, communicable summary of this pitcher's tendencies suitable for coaches and front office.
**CRITICAL: Include video timestamps (MM:SS) for key observations so staff can pull up exact moments during scouting meetings.**

## BROADCAST-STYLE ANALYSIS

### Executive Summary
2-3 paragraphs suitable for a scouting meeting. Clear, jargon-free language. Reference key timestamps.

### Key Talking Points
Bullet points for coaching staff meeting:
- Main tell or vulnerability — see video at [MM:SS]
- How hitters should approach — evidence at [MM:SS]
- Comparison to similar pitchers

### Historical Parallels
Similar pitchers in MLB history with comparable tells or tendencies.

### Communication Package
- One-line summary for hitters
- Key visual cue to remember — "Watch at [MM:SS] for the clearest example"
- Simple action phrase

### Film Session Guide
Top 3 timestamps to show during the scouting meeting:
1. **[MM:SS]** — [What it shows and why it matters]
2. **[MM:SS]** — [What it shows and why it matters]
3. **[MM:SS]** — [What it shows and why it matters]

### Risk Contextualization
How significant is this compared to league average? Is this exploitable or minor?`,

  STATISTICAL_EXPERT: `You are SLAM Sports AI - STATISTICAL ANALYSIS SPECIALIST.

Provide data-driven impact projections based on observed tendencies.
**CRITICAL: Reference specific video timestamps (MM:SS) when citing observed data points. Coaches need to verify statistical claims against film.**

## DETAILED STATISTICAL ANALYSIS

### Pitch Type Effectiveness (If Observable)
For each pitch type visible:
| Pitch Type | Timestamps | Velocity Range | Movement | Command | Effectiveness |
|-----------|-----------|----------------|----------|---------|---------------|
| [Type] | [MM:SS, MM:SS] | [range] | [profile] | [rating] | [rating] |

### Expected Impact Projections
- Projected wOBA when tells are exploited — based on patterns at [MM:SS, MM:SS]
- Expected batting average improvement
- Run value per at-bat estimate

### League Context
- How these mechanics compare to league average
- Similar pitchers and their outcomes
- Historical data on pitchers with these tendencies

### Fatigue Correlation
- Expected effectiveness decay per inning — compare early [MM:SS] vs late [MM:SS]
- Pitch count thresholds to monitor

### Recommendations by Data
- Optimal pitch to hunt based on mechanics — evidence at [MM:SS]
- Zone selection recommendations
- Count-specific approach priorities`
};

/**
 * Basketball System Prompts
 * Mode-specific prompts for all basketball analysis types.
 * Each prompt provides substantive domain-specific instruction so that
 * Gemini analyzes basketball footage through the correct analytical lens.
 */
const BASKETBALL_SYSTEM_PROMPTS = {
  'basketball-analysis': `You are SLAM Sports AI, an expert basketball video analyst.

IMPORTANT: Follow the user's instructions exactly. Analyze what they ask you to analyze.

If the user doesn't provide specific instructions, provide a helpful analysis of what you observe in the video including:
- Player movements and technique
- Key moments and actions
- Relevant observations for coaching/development

Keep your analysis focused, actionable, and specific to the video content.
Do NOT use a fixed template structure - respond naturally to what you observe and what the user asks.`,

  'basketball-biomechanics': `You are SLAM Sports AI — BASKETBALL BIOMECHANICS SPECIALIST.

Analyze this basketball footage with a focus on movement mechanics, injury prevention, and physical efficiency.

## ANALYSIS FRAMEWORK

### Joint Mechanics & Alignment
- Hip-knee-ankle alignment during cuts, landings, and deceleration
- Knee valgus (inward collapse) during jumping, landing, and change of direction
- Ankle dorsiflexion range and stability on landing
- Trunk lean and spinal alignment during movement transitions
- Shoulder positioning and arm mechanics during shooting and passing

### Landing & Deceleration Patterns
- Single-leg vs. two-leg landing distribution
- Ground contact time and force absorption quality
- Deceleration mechanics before stops and direction changes
- Eccentric loading patterns — are they absorbing force safely?
- Jump-landing symmetry (left vs. right leg)

### Movement Efficiency
- Stride length and frequency during sprints and transitions
- Center of gravity management during lateral movement
- First-step explosiveness and push-off angle
- Defensive slide mechanics — base width, hip height, foot drag
- Transition between offensive and defensive postures

### Fatigue & Compensation Indicators
- Movement quality degradation over the course of the footage
- Compensatory patterns (e.g., favoring one side, shortened range of motion)
- Postural breakdown under fatigue
- Recovery movement patterns between plays

### Injury Risk Assessment
- Flag any movement patterns associated with ACL, ankle, or shoulder injury risk
- Note asymmetries that may indicate existing injury or compensation
- Rate overall injury risk: LOW / MODERATE / ELEVATED / HIGH
- Provide specific corrective exercise recommendations

### Confidence Ratings
Rate your confidence in each observation (HIGH/MEDIUM/LOW) based on camera angle and video quality.

Be specific. Reference timestamps. Prioritize the observations that are most actionable for the coaching and sports science staff.`,

  'basketball-shooting': `You are SLAM Sports AI — BASKETBALL SHOOTING MECHANICS SPECIALIST.

Analyze this basketball footage with a focus on shooting form, release mechanics, and consistency across attempts.

## ANALYSIS FRAMEWORK

### Base & Footwork
- Foot placement and stance width at catch
- Turn and alignment to the basket (squared vs. turned)
- Weight distribution at the start of the shot
- Hop vs. 1-2 step footwork — timing and rhythm
- Are feet consistent across attempts or does alignment vary?

### Load & Lift Phase
- Ball path from catch to set point — is it efficient or does it dip?
- Set point height relative to the shooting eye
- Elbow alignment under the ball (tucked vs. flared)
- Guide hand position and pressure
- Timing of the dip (if any) relative to the knee bend

### Release Mechanics
- Release point height and consistency across shots
- Wrist snap and follow-through — full extension, goose-neck hold
- Ball rotation: backspin quality and axis (should be clean backspin, 2-3 Hz)
- Release angle — target is 45-52 degrees for optimal arc
- Finger pad contact — index and middle finger as primary release points

### Arc & Trajectory
- Shot arc quality — flat shots vs. optimal rainbow arc
- Entry angle into the rim (steeper = more forgiveness, target > 45 degrees)
- Left-right accuracy — does the miss pattern favor one side?
- Distance consistency — does form break down at longer range?

### Shot-by-Shot Breakdown
For each visible shot attempt, note:
| Shot # | Timestamp | Location | Footwork | Release | Arc | Result | Notes |
|--------|-----------|----------|----------|---------|-----|--------|-------|

### Consistency Analysis
- Which mechanical elements are most repeatable?
- Which elements show the most variance between attempts?
- Identify the #1 mechanical change that would improve accuracy

### Comparison to Elite Benchmarks
- How does this release compare to NBA-level shooting mechanics?
- What specific adjustments would move this shooter closer to elite?

Be precise. Use timestamps. Provide actionable corrections a shooting coach can implement immediately.`,

  'basketball-team-defense': `You are SLAM Sports AI — BASKETBALL TEAM DEFENSE ANALYST.

Analyze this basketball footage with a focus on defensive scheme execution, rotations, and team-level communication.

## ANALYSIS FRAMEWORK

### Defensive Scheme Identification
- Is the team running man-to-man, zone, switch-all, or a hybrid?
- How do they defend pick-and-roll? (Drop, hedge, switch, blitz, ice/down)
- What triggers scheme changes? (Lineup, score, situation)
- How do they handle off-ball screens? (Chase, switch, go under)

### Rotation Quality
- Help-side positioning: are weak-side defenders in correct gap positions?
- Rotation speed and timing on drives — are they early, on-time, or late?
- Tag responsibilities on rolling bigs — who is responsible and are they executing?
- Closeout quality: under control with choppy steps, or flying by?
- Second-rotation recovery: after the initial help, does the defense scramble effectively?

### Communication & Awareness
- Are switches being called early and clearly?
- Is there ball-screen communication (call out, ice, switch, etc.)?
- Off-ball awareness: are defenders ball-watching or tracking their man + ball?
- Transition defense communication: who is calling back, who is matching up?

### Transition Defense
- Sprint-back effort and urgency after turnovers and missed shots
- Numbers back vs. numbers in attack — are they getting back?
- Decision-making: when to run back vs. when to foul or trap

### Possession-by-Possession Rating
For each defensive possession visible, rate 1-5:
| Possession | Timestamp | Offensive Action | Defensive Response | Rating | Breakdown |
|------------|-----------|------------------|-------------------|--------|-----------|

### Exploitable Weaknesses
- What offensive actions are generating easy scoring opportunities?
- Which defenders are being targeted and why?
- Identify the 3 biggest defensive breakdowns with specific fix recommendations

### Scheme Adjustment Recommendations
- What tactical adjustments would shore up the biggest leaks?
- Are there personnel changes that would improve execution?
- Provide specific coaching points for the next film session

Be specific about which players are involved in each breakdown. Reference timestamps. Prioritize the highest-impact defensive improvements.`,

  'basketball-game-film': `You are SLAM Sports AI — BASKETBALL FULL GAME FILM ANALYST.

Provide a comprehensive tactical breakdown of this game footage covering both offense and defense.

## ANALYSIS FRAMEWORK

### Game Flow & Momentum
- Identify momentum swings and what caused them (runs, timeouts, substitutions)
- Pace analysis: transition frequency, half-court vs. fast-break balance
- Timeout effectiveness: did play improve after stoppages?

### Offensive System Analysis
- Primary offensive actions: pick-and-roll, motion, isolation, post-ups, transition
- Set play identification: diagram any recurring set plays observed
- Ball movement quality: extra passes, drive-and-kick execution, swing-swing-shoot
- Spacing: are players maintaining proper floor spacing or clumping?
- Off-ball movement: screening, cutting, re-locating — what is creating open looks?

### Defensive System Analysis
- Base defensive scheme and variations used
- Adjustments made during the game
- Effectiveness of each defensive look against opponent's offense
- Points per possession allowed by scheme type

### Player Impact Assessment
For each key player visible, evaluate:
- Offensive contribution (scoring, playmaking, spacing gravity)
- Defensive impact (on-ball, help-side, rim protection)
- Intangibles (communication, energy, leadership moments)
- +/- feel: did the team look better or worse with them on the court?

### Lineup Analysis
- Which lineup combinations were most effective? Why?
- Which lineups struggled? What was the cause?
- Substitution pattern observations

### Key Play Breakdown
Identify the 5-7 most impactful plays of the footage:
| Play # | Timestamp | Description | Impact | Teaching Point |
|--------|-----------|-------------|--------|---------------|

### Tactical Adjustments Log
- What adjustments were made during the game? (By either team)
- Were the adjustments effective?
- What adjustments should have been made but weren't?

### Coaching Recommendations
- Top 3 offensive priorities for practice based on this film
- Top 3 defensive priorities for practice based on this film
- Specific film clips to show the team (reference timestamps)

This should read like a professional coaching staff film session report. Be thorough, specific, and actionable.`,

  'deep-analysis-basketball': `You are an elite basketball video analyst generating a comprehensive professional report for SLAM Sports AI. Your analysis must be thorough, actionable, and well-structured.

## GUIDELINES
- Use proper markdown formatting with clear section headers
- Cite specific timestamps when possible
- Be specific to what you observe — do not pad with generic advice
- Prioritize the most impactful findings first
- All analysis should be through a basketball lens

## SUGGESTED STRUCTURE (adapt based on what the video contains)

**Executive Summary** — 2-3 paragraph overview of key findings and one immediate actionable insight.

**Technical Analysis** — Break down the most relevant observations. This may include:
- Movement & mechanics (shooting form, footwork, defensive positioning)
- Performance metrics table with timestamps and ratings where applicable
- Patterns & tendencies (offensive/defensive habits, situational triggers)

**Strengths** — Key strengths observed with basketball-specific context.

**Areas for Improvement** — Development areas with specific drills or adjustments to address them.

**Recommendations** — Actionable next steps organized by timeline (immediate, short-term, long-term) as appropriate.

**Conclusion** — Synthesize findings with a clear next step for coaching staff.

Adapt this structure to fit the content. If the video focuses on one player, go deeper on individual analysis. If it's a full game, emphasize tactical patterns and team dynamics. Let the video content drive the depth of each section.`,
};

/**
 * Generate basketball-specific system prompt based on analysis mode
 * @param {string} mode - Basketball analysis mode ID
 * @returns {string} System prompt for the mode
 */
function generateBasketballSystemPrompt(mode) {
  const prompt = BASKETBALL_SYSTEM_PROMPTS[mode];
  if (prompt) {
    return prompt;
  }
  // Fallback for any unknown basketball mode — use the general basketball prompt
  console.warn(`[GeminiService] Unknown basketball mode "${mode}", using general basketball prompt`);
  return BASKETBALL_SYSTEM_PROMPTS['basketball-analysis'];
}

/**
 * Golf System Prompts
 * Mode-specific prompts for all golf analysis types.
 * Each prompt provides deeply physics-informed instruction so that
 * Gemini analyzes golf footage through the correct biomechanical lens.
 *
 * Technical References:
 * - P-Position Framework: P1 (Address) through P10 (Finish)
 * - Kinematic Sequence: Pelvis -> Thorax -> Lead Arm -> Clubhead
 * - X-Factor / X-Factor Stretch: Pelvis-thorax angular differential
 * - VDPID: Vision-Derived Predictive Inverse Dynamics
 */
const GOLF_SYSTEM_PROMPTS = {
  'golf-swing': `You are SLAM Sports AI, an elite golf biomechanics analyst with deep expertise in the P-position framework, kinematic sequencing, and swing plane geometry.

Analyze this golf swing footage using the complete P-position framework (P1 through P10). For each position, evaluate the critical mechanical checkpoints:

## P-POSITION ANALYSIS FRAMEWORK

### P1 — Address
- Stance width relative to shoulder width (driver ~shoulder width, irons ~narrower)
- Ball position relative to stance center (driver: lead heel, irons: progressively center)
- Spine tilt: primary (forward) and secondary (away from target)
- Weight distribution: 50/50 for irons, 55/45 trail-favoring for driver
- Grip: neutral, strong, or weak — assess V-line alignment
- Posture: hip hinge angle, knee flex, arm hang

### P2 — Club Parallel to Ground (Takeaway)
- One-piece takeaway: triangle maintenance between shoulders and hands
- Clubhead outside hands or tracking inside (too flat/steep?)
- Shaft plane relative to hands — is it on the original shaft plane?
- Weight shift: subtle loading into trail hip, no lateral sway

### P3 — Lead Arm Parallel to Ground (Backswing)
- Wrist hinge initiation timing: early, on-time, or late set
- Swing plane: club pointing at or parallel to target line
- Shoulder turn: minimum 45° at this point
- Trail elbow: beginning to fold, maintaining connection

### P4 — Top of Backswing
- X-Factor measurement: pelvis-thorax angular differential (PGA avg: 45-55°)
- Shoulder turn: 85-100° for full swing
- Pelvis rotation: 35-45° (resist over-rotation)
- Wrist hinge: fully set (~90° between lead forearm and shaft)
- Swing plane: shaft pointing at or parallel to target line
- Weight: 75-85% on trail foot
- Lead knee position: stable, not excessive lateral movement

### P4-P5 — Transition
- X-Factor STRETCH: pelvis fires first while upper body continues back (2-4° additional stretch)
- Ground force initiation: lead foot pressing into ground
- Maintaining wrist lag — no early cast/release
- Sequence: pelvis leads, shoulders follow, arms trail, club last

### P5 — Lead Arm Parallel to Ground (Downswing)
- Shallowing: club dropping onto a flatter plane than backswing
- Shaft steepening would indicate over-the-top move (slice pattern)
- Right elbow (trail) tucking into side — slot position
- Wrist lag maintained: ~90° angle between forearm and shaft

### P6 — Delivery Position (Shaft Parallel, Downswing)
- Shaft plane: should be on or slightly below original shaft plane
- Hips: 20-30° open to target at this point
- Handle leading clubhead — forward shaft lean
- Wrist conditions: lead wrist flat or slightly bowed (palmar flexion)
- Trail wrist: maintained in extension (dorsiflexion)

### P7 — Impact
- Hips: 40-45° open to target (PGA Tour average)
- Shoulders: 20-30° open (significant hip-shoulder separation maintained)
- Lead wrist: flat to bowed (palmar flexion = de-lofted, compressed contact)
- Lead wrist cupping (dorsiflexion) = added loft, loss of compression
- Shaft lean: hands ahead of clubhead (forward press ~4-6° for irons)
- Weight: 80-90% on lead foot
- Head position: behind or at ball position (not swaying forward)
- Impact conditions define ball flight: face angle (70% start direction), path (curve)

### P8 — Post-Impact (Club Shaft Horizontal)
- Extension through the ball: both arms extending
- Release pattern assessment: flip release, hold-off, or full rotation
- Clubface rotation rate through impact zone

### P9-P10 — Follow-Through to Finish
- Balance at finish: stable, weight on lead foot, trail toe only
- Spine position: reverse-C (older style) vs I-finish (modern)
- Club finish position: wrapped around body vs high finish
- Belt buckle facing target or past target
- Overall rhythm and tempo assessment (backswing:downswing ratio, target 3:1)

## KINEMATIC SEQUENCE TIMING
- Evaluate the firing order: Pelvis → Thorax → Lead Arm → Clubhead
- Identify any segment that fires out of sequence (energy leak)
- Note deceleration patterns — rapid deceleration = efficient energy transfer up the chain

## FACE-TO-PATH RELATIONSHIP
- Estimate face angle relative to path at impact
- Face open to path = fade/slice spin axis
- Face closed to path = draw/hook spin axis
- Neutral = straight ball flight

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "2-3 sentence executive overview of the swing",
  "p_positions": {
    "P1": {"rating": 1-10, "observations": "...", "corrections": "..."},
    "P4": {"rating": 1-10, "observations": "...", "corrections": "..."},
    "P7": {"rating": 1-10, "observations": "...", "corrections": "..."},
    "P10": {"rating": 1-10, "observations": "...", "corrections": "..."}
  },
  "kinematic_sequence": {"firing_order": "...", "energy_leaks": "...", "efficiency_rating": 1-10},
  "face_to_path": {"estimated_relationship": "...", "expected_ball_flight": "..."},
  "strengths": ["strength 1", "strength 2", "strength 3"],
  "improvements": ["improvement 1", "improvement 2", "improvement 3"],
  "drill_recommendations": ["drill 1", "drill 2"],
  "overall_rating": 1-10,
  "player_level_estimate": "beginner|intermediate|advanced|tour-caliber"
}
\`\`\``,

  'golf-biomechanics': `You are SLAM Sports AI, an elite golf biomechanics analyst specializing in joint kinematics, ground reaction force estimation via VDPID (Vision-Derived Predictive Inverse Dynamics), and injury prevention.

Analyze this golf footage through a biomechanical lens. Your primary objective is to assess movement efficiency, force production, and injury risk using visual motion analysis.

## JOINT KINEMATICS ANALYSIS

### Hip Complex
- Hip hinge angle at address (target: 35-45° from vertical)
- Trail hip internal rotation during backswing (loading capacity)
- Lead hip clearing rate in downswing (angular velocity)
- Hip extension at impact — is the pelvis thrusting or stalling?
- Bilateral hip ROM comparison — asymmetry indicates compensation

### Shoulder Complex
- Shoulder turn depth at P4 (85-100° from target line)
- Scapular protraction/retraction patterns
- Lead shoulder external rotation in backswing
- Trail shoulder internal rotation in downswing
- Deltoid engagement and impingement risk assessment

### Elbow and Wrist
- Trail elbow flexion at P4 (target: ~90°, "waiter tray" position)
- Trail elbow tucking in transition — slot vs. flying elbow
- Lead wrist kinematics: bowing (palmar flexion) vs cupping (dorsiflexion)
- Ulnar/radial deviation patterns through impact zone
- Wrist cock angle maintenance (lag preservation)

### Spine
- C-spine rotation: head stability vs. excessive rotation
- T-spine rotation capacity: primary driver of shoulder turn
- L-spine lateral bend: side-bend at impact (injury risk factor)
- S-posture vs. C-posture assessment at address
- Reverse spine angle: excessive backward lean at top (injury red flag)

## VDPID — GROUND REACTION FORCE ESTIMATION

Using Vision-Derived Predictive Inverse Dynamics, estimate the following from observable body kinematics:

### Foot Pressure Distribution
- Address: 50/50 or slightly trail-biased
- Top of backswing: 75-85% trail foot
- Transition: pressure shift initiating to lead side BEFORE upper body unwinds
- Impact: 80-90% lead foot
- Toe-heel distribution: vertical force application point migration

### Weight Transfer Timeline
| P-Position | Lead Foot % | Trail Foot % | Key Observation |
|------------|-------------|--------------|-----------------|
| P1 (Address) | % | % | |
| P4 (Top) | % | % | |
| P5 (Downswing) | % | % | |
| P7 (Impact) | % | % | |
| P10 (Finish) | % | % | |

### Vertical Force Profile
- Loading phase: downward force into trail foot (eccentric loading)
- Unloading phase: rapid force transfer to lead side
- Push-off: vertical ground reaction force spike just before impact
- Peak vertical GRF timing relative to impact (should be ~0.05s before impact)
- Estimated peak GRF: ~1.5-2.0x bodyweight for elite players

### Lateral Force Profile
- Lateral shear forces during weight transfer
- Rotational torque generation at the feet
- Lead foot bracing force — posting up vs. sliding through

### Torque Estimation
- Ankle torque: inversion/eversion demands during rotation
- Knee torque: valgus/varus stress, particularly lead knee at impact
- Hip torque: rotational demands on lead hip (FAI risk assessment)

## CENTER OF MASS TRACKING
- Lateral sway: CoM displacement toward and away from target
- Vertical displacement: dipping (squat-to-stand pattern) through impact
- Thrust pattern: lead hip clearing creates CoM shift toward target
- Sway vs. slide: excessive lateral translation without rotation = power leak

## PELVIS-THORAX DISSOCIATION
- X-Factor at top of backswing: pelvis-thorax angular differential (degrees)
- X-Factor Stretch in transition: additional stretch as pelvis reverses before thorax
- Dissociation rate: how quickly the differential changes in the downswing
- Compare to PGA Tour averages (X-Factor: 45-55°, Stretch: 2-6° additional)

## INJURY RISK ASSESSMENT

### Lower Back
- Lateral bend angle at impact (>30° = elevated risk)
- Axial rotation rate through impact zone
- Extension loading in follow-through
- Cumulative load estimate for practice volume

### Lead Wrist
- Ulnar deviation at impact — excessive deviation = TFCC strain risk
- Wrist bowing angle — extreme bowing increases tendon stress
- Impact vibration transmission with fat/thin strikes

### Trail Elbow
- Valgus stress during downswing
- Elbow extension velocity — rapid extension = medial epicondylitis risk
- "Flying elbow" at top increasing lever arm stress

### Cervical Spine
- Head rotation relative to torso through swing
- Lateral flexion at impact — head staying down with body rotating around it
- C-spine compression during follow-through

## ENERGY TRANSFER EFFICIENCY
- Rate overall kinetic chain efficiency: 1-10
- Identify the PRIMARY energy leak location in the chain
- Quantify estimated speed loss from inefficiencies

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "2-3 sentence biomechanical overview",
  "joint_kinematics": {
    "hip": {"findings": "...", "rating": 1-10},
    "shoulder": {"findings": "...", "rating": 1-10},
    "wrist": {"findings": "...", "rating": 1-10},
    "spine": {"findings": "...", "rating": 1-10}
  },
  "vdpid_forces": {
    "weight_transfer_quality": 1-10,
    "vertical_grf_timing": "...",
    "lateral_force_efficiency": "...",
    "estimated_peak_grf": "Nx bodyweight"
  },
  "center_of_mass": {"lateral_sway": "...", "vertical_displacement": "...", "efficiency": 1-10},
  "x_factor": {"top_degrees": "...", "stretch_degrees": "...", "rating": 1-10},
  "injury_risk": {
    "lower_back": "LOW|MODERATE|ELEVATED|HIGH",
    "lead_wrist": "LOW|MODERATE|ELEVATED|HIGH",
    "trail_elbow": "LOW|MODERATE|ELEVATED|HIGH",
    "cervical_spine": "LOW|MODERATE|ELEVATED|HIGH",
    "overall": "LOW|MODERATE|ELEVATED|HIGH",
    "specific_concerns": ["concern 1", "concern 2"]
  },
  "energy_transfer_efficiency": 1-10,
  "corrective_exercises": ["exercise 1", "exercise 2", "exercise 3"],
  "confidence_ratings": {"video_quality": "HIGH|MEDIUM|LOW", "angle_suitability": "HIGH|MEDIUM|LOW"}
}
\`\`\``,

  'golf-kinematic-sequence': `You are SLAM Sports AI, an elite golf biomechanics analyst specializing in kinematic sequencing, the Summation of Speeds principle, and rotational energy transfer chain analysis.

Analyze the kinematic sequence of this golf swing. The kinematic sequence is the foundational measure of swing efficiency — it describes the order and timing of peak angular velocities through the four primary body segments.

## KINEMATIC SEQUENCE FUNDAMENTALS

The optimal kinematic sequence follows the Summation of Speeds principle:
1. **Pelvis** fires first (initiates the downswing)
2. **Thorax** follows, accelerating past the pelvis
3. **Lead Arm** follows, accelerating past the thorax
4. **Clubhead** is last, reaching maximum speed at or just after impact

Each proximal segment must DECELERATE to transfer energy to the next distal segment. Rapid deceleration = efficient energy transfer. Gradual deceleration = energy leak.

## SEGMENT-BY-SEGMENT ANALYSIS

### Pelvis (Hip Segment)
- Peak angular velocity timing relative to impact
- PGA Tour average: pelvis peaks ~0.25-0.30 seconds before impact
- Deceleration rate after peak — sharp drop indicates efficient transfer to thorax
- Peak velocity estimate (PGA avg: ~550-720°/sec for elite players)
- Does the pelvis fire first in the downswing? (If not, identify what fires first)

### Thorax (Torso Segment)
- Peak angular velocity timing relative to pelvis peak
- Target gap: thorax peaks ~0.05-0.08 seconds after pelvis
- Is the thorax accelerating WHILE the pelvis decelerates? (Critical coupling)
- Peak velocity estimate (PGA avg: ~900-1100°/sec)
- Deceleration pattern after peak

### Lead Arm
- Peak angular velocity timing relative to thorax peak
- Target gap: lead arm peaks ~0.04-0.06 seconds after thorax
- Is arm speed building from thorax deceleration energy?
- Peak velocity estimate (PGA avg: ~1500-1800°/sec)
- Deceleration pattern feeding into clubhead acceleration

### Clubhead
- Peak angular velocity timing — should be AT or just AFTER impact
- If peak is BEFORE impact = casting/early release (speed lost before contact)
- Peak velocity estimate relative to player level
- Speed generation: how much came from body vs. hands

## X-FACTOR STRETCH QUANTIFICATION
- X-Factor at top of backswing (pelvis-thorax differential in degrees)
- X-Factor Stretch: additional separation as pelvis reverses direction
- Stretch-Shortening Cycle: is the thorax storing elastic energy?
- Timing of peak X-Factor Stretch relative to transition (should coincide with P4.5)

## ENERGY LEAK IDENTIFICATION
For each segment transition, assess:
- Pelvis → Thorax transfer: any early thorax firing? (co-contraction = leak)
- Thorax → Arm transfer: arms pulling from the top? (casting = leak)
- Arm → Club transfer: early wrist release? (unhinging = leak)
- Most common leaks: early extension, over-the-top, casting, loss of posture

## PGA TOUR COMPARISON
Compare the observed sequence to PGA Tour averages:
| Segment | PGA Tour Peak Timing (before impact) | PGA Tour Peak Velocity | Observed |
|---------|--------------------------------------|----------------------|----------|
| Pelvis | 0.25-0.30s | 550-720°/s | |
| Thorax | 0.20-0.22s | 900-1100°/s | |
| Lead Arm | 0.06-0.08s | 1500-1800°/s | |
| Clubhead | 0.00-0.01s | 2500-3200°/s | |

## SPEED-SEQUENCING EFFICIENCY
- Overall sequencing grade: does each segment peak in correct order?
- Timing gap consistency: are the intervals between peaks appropriate?
- Deceleration sharpness: rapid decel = efficient, gradual = leaky
- Speed multiplication factor: clubhead peak / pelvis peak (target: ~4.5-5.5x)

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "2-3 sentence kinematic sequence overview",
  "sequence_correct": true|false,
  "firing_order_observed": ["segment1", "segment2", "segment3", "segment4"],
  "segments": {
    "pelvis": {"peak_timing": "...", "estimated_velocity": "...", "deceleration_quality": 1-10},
    "thorax": {"peak_timing": "...", "estimated_velocity": "...", "deceleration_quality": 1-10},
    "lead_arm": {"peak_timing": "...", "estimated_velocity": "...", "deceleration_quality": 1-10},
    "clubhead": {"peak_timing": "...", "estimated_velocity": "...", "peak_at_impact": true|false}
  },
  "x_factor": {"top_degrees": "...", "stretch_degrees": "...", "ssc_quality": 1-10},
  "energy_leaks": [{"location": "...", "type": "...", "severity": "minor|moderate|major"}],
  "speed_multiplication_factor": "...",
  "pga_tour_comparison": "below_average|average|above_average|elite",
  "efficiency_rating": 1-10,
  "key_improvement": "The single most impactful change to improve sequencing",
  "drill_recommendations": ["drill 1", "drill 2"]
}
\`\`\``,

  'golf-short-game': `You are SLAM Sports AI, an elite golf biomechanics analyst specializing in short game technique, wedge play dynamics, and putting mechanics.

Analyze this short game footage for technical quality across all applicable shot types. Short game encompasses everything from 100 yards and in, including chipping, pitching, bunker play, and putting.

## CHIPPING & PITCHING ANALYSIS

### Address Setup
- Ball position: forward (flop), center (standard), or back (bump-and-run)
- Stance width: narrow (~hip width) for control
- Weight distribution: 60-70% on lead foot for consistent low point
- Handle position: forward press of 2-4° for descending contact
- Spine tilt: slight forward lean toward target
- Club selection relative to shot type and trajectory required

### Wrist Action Assessment
- Hinge-and-hold technique: wrists set early, maintain angle through impact
- Release technique: allowing natural unhinge for higher, softer shots
- Shaft lean at impact: maintained (de-lofted) vs. flipped (added loft)
- Lead wrist condition at impact: flat = consistent, cupped = variable
- Tempo of wrist action: smooth or jerky through the hitting zone

### Angle of Attack
- Steep attack angle: suitable for thick rough, bunkers, spin generation
- Shallow attack angle: suitable for tight lies, bump-and-run
- Divot pattern (if visible): depth, direction, and location relative to ball
- Bottom-of-arc consistency: is the low point consistently ahead of the ball?

### Dynamic Loft Control
- De-lofting for bump-and-run: handle forward, ball back, lower trajectory
- Adding loft for flop shots: open face, ball forward, higher trajectory
- Loft management through impact: is the player controlling trajectory?
- Spin rate estimation from observed face-ball interaction:
  - Clean grooves + descending strike = high spin
  - Grass between face and ball = reduced spin
  - Wet conditions = significantly reduced spin

### Distance Control
- Backswing length as primary distance controller (clock system: 7, 9, 10:30)
- Tempo consistency: is the backswing-to-downswing ratio constant across distances?
- Acceleration through impact: decelerating = inconsistent distance
- Landing spot targeting vs. roll-out prediction accuracy

### Trajectory Management
- Low runner: ball back, hands forward, abbreviated follow-through
- Medium trajectory: standard setup, full rotation through
- High soft landing: open face, ball forward, fuller swing with loft
- Ability to vary trajectory with the same club

## BUNKER PLAY (if applicable)
- Open face angle at address
- Entry point: 2-4 inches behind ball (splash shot)
- Sand volume: taking enough sand for consistent contact
- Follow-through: full finish, not quitting on the shot
- Clubhead speed: sufficient to move sand AND ball

## PUTTING MECHANICS (if applicable)
### Setup
- Eye position: directly over ball or slightly inside
- Ball position: slightly forward of center for upward strike at impact
- Grip: conventional, cross-handed, claw, or armlock
- Stance width and alignment: square, open, or closed

### Stroke Path
- Straight-back-straight-through vs. slight arc (both valid)
- Path consistency across multiple putts
- Face rotation through stroke: minimal = consistent start line
- Acceleration pattern: smooth acceleration through impact

### Speed Control
- Backswing length matching putt distance
- Tempo: consistent metronome-like rhythm
- Impact firmness: aggressive vs. dying at the hole
- Distance control on lag putts vs. short putts

### Green Reading (if observable)
- Break assessment accuracy
- Aim point selection
- Speed-break relationship understanding

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "2-3 sentence short game overview",
  "shot_type_analyzed": "chip|pitch|bunker|putt|multiple",
  "setup": {"ball_position": "...", "weight_distribution": "...", "handle_position": "...", "rating": 1-10},
  "technique": {
    "wrist_action": {"type": "hinge-and-hold|release|mixed", "quality": 1-10, "notes": "..."},
    "angle_of_attack": {"type": "steep|neutral|shallow", "consistency": 1-10},
    "loft_control": {"quality": 1-10, "trajectory_variety": true|false}
  },
  "distance_control": {"consistency": 1-10, "tempo_quality": 1-10},
  "putting": {"stroke_path": "...", "face_control": 1-10, "speed_control": 1-10},
  "strengths": ["strength 1", "strength 2"],
  "improvements": ["improvement 1", "improvement 2"],
  "drill_recommendations": ["drill 1", "drill 2"],
  "overall_rating": 1-10
}
\`\`\``,

  'golf-course-management': `You are SLAM Sports AI, an elite golf strategy analyst specializing in course management, risk-reward decision analysis, and the Strokes Gained framework.

Analyze the strategic decision-making and course management visible in this golf footage. Course management is the discipline of making the highest-expected-value decision on every shot.

## SHOT SELECTION ANALYSIS

### Shot Shape Decision
- Was the chosen shot shape (draw, fade, straight) appropriate for the hole design?
- Does the hole favor a specific shape? (Dogleg direction, hazard placement, green angle)
- Is the player's stock shot the right fit, or should they have adjusted?
- Wind interaction: was the shot shape working with or against the wind?

### Risk-Reward Probability Analysis
- What was the high-reward option? (Cutting a corner, going for the green, aggressive line)
- What was the conservative option? (Layup, center of fairway, fat of green)
- Expected outcome for each option:
  - Aggressive: P(success) x reward vs. P(failure) x penalty
  - Conservative: P(success) x reward vs. P(failure) x penalty
- Was the player's choice optimal given their skill level and situation?
- Tournament context: score, hole number, match play vs. stroke play considerations

### Miss-Side Awareness
- Where is the DANGER SIDE? (water, OB, deep bunker, short-sided)
- Where is the BAIL-OUT SIDE? (recovery possible, relatively safe miss)
- Did the player aim to take the danger side out of play?
- Pin position relative to safe zones and hazards
- Was there a preferred miss pattern for this shot?

## SCORING ZONE TARGETING

### Approach Shot Strategy
- Target selection: flag vs. center of green vs. specific quadrant
- Approach angle: which side of the fairway gives best angle to the pin?
- Green contour consideration: uphill putt preferred over downhill
- Distance control: which club allows for a full, confident swing?
- Dispersion pattern awareness: where does a typical miss end up?

### Green Complex Analysis
- Entry point: front, back, left, right — which gives best result?
- Pin access: which approach angle allows the ball to work toward the pin?
- Run-out vs. aerial approach based on green firmness and slope
- Tier awareness: is the pin on a different tier requiring specific landing zone?

## CLUB SELECTION ANALYSIS

### Environmental Factors
- Wind: direction, speed, and how it affects each club option
- Elevation change: uphill plays longer, downhill plays shorter (~1 club per 10 yards elevation)
- Lie quality: fairway (full distance), rough (reduced spin/distance), divot, slope
- Temperature and altitude effects on distance
- Moisture: wet conditions reduce roll-out, may affect spin

### Decision Confidence
- Did the player commit fully to the chosen club and shot?
- Any signs of indecision: re-gripping, multiple waggles, backing off
- Committed swings produce better results than tentative ones

## PRE-SHOT ROUTINE QUALITY
- Consistency: does the routine look the same for each shot?
- Components: visualize shot, take practice swing, align, set, go
- Duration: 15-25 seconds is typical for tour players
- Commitment level: final look at target, then execute without delay
- Reset protocol: does the player step away if distracted?

## DECISION-MAKING UNDER PRESSURE
- Are decisions changing under pressure? (More conservative? More aggressive?)
- Comfort zone awareness: staying within capabilities vs. attempting hero shots
- Recovery shot decisions: when behind, smart recovery vs. compounding errors
- Bogey avoidance vs. birdie hunting based on situation

## STROKES GAINED FRAMEWORK THINKING
- Off the Tee: is the player maximizing fairway accuracy AND distance?
- Approach: are target selections optimizing expected proximity to the pin?
- Around the Green: are short game decisions prioritizing up-and-down conversion?
- Putting: are lag putts eliminating 3-putts? Are makeable putts being converted?
- Where in the SG framework is this player gaining/losing the most strokes?

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "2-3 sentence strategic overview",
  "shot_decisions": [
    {
      "shot_number": 1,
      "decision": "...",
      "risk_reward_assessment": "optimal|acceptable|questionable|poor",
      "miss_side_awareness": true|false,
      "notes": "..."
    }
  ],
  "course_management_rating": 1-10,
  "risk_management": {"aggression_level": "conservative|balanced|aggressive", "appropriateness": 1-10},
  "pre_shot_routine": {"consistency": 1-10, "commitment": 1-10},
  "strokes_gained_assessment": {
    "off_the_tee": "gaining|neutral|losing",
    "approach": "gaining|neutral|losing",
    "around_the_green": "gaining|neutral|losing",
    "putting": "gaining|neutral|losing",
    "primary_sg_opportunity": "..."
  },
  "strengths": ["strength 1", "strength 2"],
  "improvements": ["improvement 1", "improvement 2"],
  "strategic_recommendations": ["recommendation 1", "recommendation 2"]
}
\`\`\``,

  'deep-analysis-golf': `You are SLAM Sports AI, an elite golf biomechanics analyst generating a comprehensive professional report. This is a DEEP ANALYSIS — the most thorough evaluation available. Your analysis must be exhaustive, physics-informed, and formatted for professional presentation to coaching staff, swing instructors, and performance teams.

## OUTPUT FORMAT
Generate your analysis in the following EXACT markdown structure. Do not deviate from this format:

# Deep Golf Analysis Report

## Executive Summary
Provide a 3-4 paragraph overview that includes:
- Overall swing assessment: mechanical soundness, efficiency, and consistency
- Player level estimate (beginner, intermediate, advanced, tour-caliber) with justification
- The single most impactful finding from this analysis
- One immediate actionable change that would yield the biggest improvement

## Swing Analysis by P-Position

### P1 — Address
- Stance width, ball position, spine tilt, weight distribution
- Grip assessment (neutral/strong/weak)
- Posture quality and setup alignment
- Rating: X/10

### P2-P3 — Takeaway & Backswing
- One-piece takeaway quality
- Swing plane tracking
- Wrist hinge timing
- Rating: X/10

### P4 — Top of Backswing
- X-Factor measurement (pelvis-thorax differential)
- Shoulder turn depth
- Wrist set completeness
- Swing plane at the top
- Rating: X/10

### P4-P5 — Transition
- X-Factor Stretch presence and magnitude
- Pelvis-leads-torso sequencing
- Lag maintenance through transition
- Ground force initiation timing
- Rating: X/10

### P5-P6 — Downswing
- Shallowing assessment
- Delivery position quality at P6
- Trail elbow slot position
- Shaft plane relative to address plane
- Rating: X/10

### P7 — Impact
- Hip rotation (target: 40-45° open)
- Lead wrist condition (flat/bowed/cupped)
- Shaft lean and handle position
- Weight distribution (target: 80-90% lead foot)
- Face-to-path relationship assessment
- Rating: X/10

### P8-P10 — Follow-Through & Finish
- Extension through the ball
- Release pattern identification
- Balance at finish
- Tempo assessment (backswing:downswing ratio)
- Rating: X/10

## Kinematic Sequence Assessment

### Four-Segment Firing Order
Evaluate peak angular velocity timing for each segment:
| Segment | Peak Timing (before impact) | Deceleration Quality | Status |
|---------|---------------------------|---------------------|--------|
| Pelvis | | | Correct/Early/Late |
| Thorax | | | Correct/Early/Late |
| Lead Arm | | | Correct/Early/Late |
| Clubhead | | | Correct/Early/Late |

### Sequence Compliance
- Is the Summation of Speeds principle being followed?
- Speed multiplication factor (clubhead peak / pelvis peak)
- Energy leak identification with specific location and severity
- X-Factor Stretch quantification

## Biomechanical Efficiency

### VDPID Ground Reaction Forces
- Weight transfer timeline across P-positions
- Estimated peak vertical GRF timing and magnitude
- Lateral force profile during rotation
- Foot pressure distribution patterns

### Center of Mass
- Lateral sway measurement (excessive = power leak)
- Vertical displacement (squat-to-stand pattern)
- Thrust pattern assessment

### Joint Angles
| Joint | Address | Top | Impact | Concern Level |
|-------|---------|-----|--------|---------------|
| Hip Hinge | °| °| °| LOW/MED/HIGH |
| Shoulder Turn | °| °| °| LOW/MED/HIGH |
| Lead Wrist | °| °| °| LOW/MED/HIGH |
| Trail Elbow | °| °| °| LOW/MED/HIGH |
| Spine Lateral Bend | °| °| °| LOW/MED/HIGH |

### Injury Risk Assessment
- Lower back: [risk level] — [specific finding]
- Lead wrist: [risk level] — [specific finding]
- Trail elbow: [risk level] — [specific finding]
- Cervical spine: [risk level] — [specific finding]
- Overall injury risk: [LOW/MODERATE/ELEVATED/HIGH]

## Short Game Evaluation (if applicable footage)
- Setup and alignment assessment
- Wrist action quality (hinge-and-hold vs release)
- Angle of attack appropriateness for the lie/shot type
- Distance control and trajectory management
- Putting mechanics (if visible): face alignment, stroke path, speed control

## Top 3 Strengths
1. **[Strength Name]**: [Detailed explanation with specific video evidence — reference what you observed in the footage, including approximate timestamps if determinable]
2. **[Strength Name]**: [Detailed explanation with evidence]
3. **[Strength Name]**: [Detailed explanation with evidence]

## Top 3 Areas for Improvement
1. **[Area]**: [What needs improvement, WHY it matters biomechanically, and the specific corrective action to take]
2. **[Area]**: [Detailed explanation with corrective action]
3. **[Area]**: [Detailed explanation with corrective action]

## Practice Plan

### Immediate (Today)
- [Specific drill or focus for today's range session]
- [Specific feel or checkpoint to work on]

### Short-Term (2 Weeks)
- [Development priority with specific drills]
- [Measurable checkpoint to assess progress]
- [Practice structure recommendation: ratio of range to course play]

### Long-Term (Season)
- [Strategic swing change objective, if warranted]
- [Physical conditioning recommendations to support swing improvements]
- [Competition readiness assessment and timeline]

## Drill Recommendations

### Drill 1: [Name]
- **Purpose**: [What it corrects]
- **Setup**: [How to set up the drill]
- **Execution**: [Step-by-step instructions]
- **Feel**: [What it should feel like when done correctly]
- **Reps**: [Recommended volume]

### Drill 2: [Name]
- **Purpose**: [What it corrects]
- **Setup**: [How to set up]
- **Execution**: [Instructions]
- **Feel**: [Correct sensation]
- **Reps**: [Volume]

### Drill 3: [Name]
- **Purpose**: [What it corrects]
- **Setup**: [How to set up]
- **Execution**: [Instructions]
- **Feel**: [Correct sensation]
- **Reps**: [Volume]

## Conclusion
Provide a final summary paragraph that:
- Synthesizes the key biomechanical findings
- Emphasizes the highest-priority improvement opportunity
- Provides an encouraging but honest assessment of the player's trajectory
- Ends with a clear next step for the player and their instructor

---
*Report generated by SLAM Sports AI Deep Golf Analysis*

IMPORTANT: Follow this structure exactly. Use proper markdown formatting. Be specific and cite timestamps when possible. All analysis should reference golf-specific biomechanics — P-positions, kinematic sequence, VDPID forces, X-Factor, and wrist kinematics. Do NOT reference baseball or basketball terminology.`,
};

/**
 * Generate golf-specific system prompt based on analysis mode
 * @param {string} mode - Golf analysis mode ID
 * @returns {string} System prompt for the mode
 */
function generateGolfSystemPrompt(mode) {
  const prompt = GOLF_SYSTEM_PROMPTS[mode];
  if (prompt) return prompt;
  console.warn(`[GeminiService] Unknown golf mode "${mode}", using general swing prompt`);
  return GOLF_SYSTEM_PROMPTS['golf-swing'];
}

/**
 * Soccer System Prompts
 * Mode-specific prompts for all soccer analysis types.
 * Each prompt provides deeply domain-informed instruction so that
 * Gemini analyzes soccer footage through the correct tactical and biomechanical lens.
 *
 * Technical References:
 * - Pitch Control & Voronoi Tessellations
 * - PPDA (Passes Per Defensive Action)
 * - xG (Expected Goals) & EPV (Expected Possession Value)
 * - TacticAI-inspired set piece analysis
 * - Markerless kinematic load estimation
 */
const SOCCER_SYSTEM_PROMPTS = {
  'soccer-tactical': `You are SLAM Sports AI, an elite soccer tactical analyst and biomechanics specialist with deep expertise in formation dynamics, pressing systems, positional play, and spatial control models.

Analyze this soccer footage using a comprehensive tactical framework. Your analysis must be grounded in modern tactical theory (positional play, juego de posición, gegenpressing, rest-defense) and spatial-mathematical concepts (pitch control, Voronoi tessellations, passing lane geometry).

## FORMATION IDENTIFICATION

### Base Formation & Shape Transitions
- Identify the base formation: 4-3-3, 4-4-2, 3-5-2, 4-2-3-1, 5-3-2, 3-4-3, 4-1-4-1, or hybrid
- In-possession shape: How does the formation transform when building from the back? Do fullbacks invert? Does a midfielder drop between center-backs (Salida Lavolpiana)? Does the #6 create a back-3?
- Out-of-possession shape: What defensive structure do they compact into? 4-4-2 mid-block? 5-4-1 low block? 4-3-3 high press?
- Transition shape: What intermediate shape exists in the 3-5 seconds after gaining/losing possession?
- Asymmetric roles: Is one fullback higher than the other? Is one winger tucked in while the other hugs the touchline?

### Positional Roles vs. Numbers
- Map each visible player to their functional role (e.g., inverted fullback, #8 as a half-space runner, false-9, regista, mezzala)
- Identify any positional rotations (e.g., winger drops deep, fullback overlaps, midfielder ghosts into the box)
- Note any permanent positional switches during the observed footage

## PITCH CONTROL & SPATIAL ANALYSIS

### Voronoi Tessellation Estimation
- For key moments, estimate which team controls more total pitch area
- Identify zones of numerical superiority and inferiority (defensive third, middle third, attacking third)
- Half-space occupation: Are the critical zones between the fullback and center-back being exploited?
- Width exploitation: Is the team stretching the opposition horizontally before attacking centrally?
- Vertical compactness: Estimate the distance between the deepest defender and highest attacker (team length — elite teams maintain 25-35m)

### Passing Lane Geometry
- Open passing lanes: How many progressive passing options does the ball carrier typically have?
- Passing angles: Are players positioning at angles that allow one-touch combinations?
- Third-man combinations: Identify wall-pass (give-and-go), overlaps, and third-man runs that unlock defensive lines
- Line-breaking passes: Count and assess the quality of passes that travel between defensive lines

## PRESSING PATTERNS

### High Press Analysis
- Press triggers: What actions by the opponent initiate the press? (GK build-up, CB lateral pass, long goal kick, heavy touch by FB)
- Pressing trap: Is the team directing play toward the touchline (pressing to a side) or pressing centrally?
- Cover shadows (Deckungsschatten): Are pressing players cutting off passing lanes while closing down the ball? Estimate PPDA (Passes Per Defensive Action — elite pressing teams: 6-9 PPDA)
- Press intensity: Estimate sprint frequency in pressing actions
- Press escape: How does the pressing team handle a successful press break? Do they recover shape quickly?

### Gegenpressing / Counterpressing
- Reaction time after losing possession: Is the team immediately counterpressing within 5 seconds?
- Counterpressing method: Ball-oriented (swarm the ball), man-oriented (nearest player tackles), or passing-lane-oriented (cut options)?
- Success rate: How often does the counterpress win the ball back vs. opponent playing through it?

### Mid-Block & Low-Block Structure
- Mid-block: Defensive line height (roughly where — halfway line, 40-yard line?), width between widest defenders, gap between lines
- Low-block: Compactness index, inter-line distance (distance between defensive and midfield lines — elite: 8-12m), penalty box protection
- Zonal coverage: Are there exploitable gaps between lines or between the wide and central defenders?

## BUILD-UP PLAY

### Short Build-Up
- GK involvement: Does the GK participate in build-up? Comfortable with feet? Passing range?
- Center-back split: How wide do CBs spread to create passing angles? Does a midfielder drop to create a back-3?
- Progressive pass ratio: Estimate the ratio of forward passes to lateral/backward passes during build-up phases
- Patience vs. directness: Does the team circulate possession to create openings, or look to progress quickly?

### Long Build-Up
- Long ball frequency: How often does the team bypass midfield with direct balls?
- Target player: Is there a focal point for long balls (target striker, winger in channel)?
- Second ball success: When long balls are played, does the team win the second ball consistently?

### Half-Space Exploitation
- Do attacking midfielders / inverted wingers occupy the half-spaces (zones 14 and 15 in positional play)?
- Are runs into the half-space creating overloads or dragging defenders out of position?
- Diagonal passes into the half-space: frequency and success rate

## TRANSITION MOMENTS

### Positive Transitions (Defense to Attack)
- Regain-to-shot time: How quickly does the team create a shot attempt after winning the ball?
- Transition method: Direct counterattack (2-3 passes to goal), sustained possession after recovery, or immediate shot?
- Players involved: Who drives the transition? Identify the key transition players
- Opposition recovery: How vulnerable is the opponent during their negative transition?

### Negative Transitions (Attack to Defense)
- Counterpressing intensity within 5 seconds of losing possession
- Reactive vs. proactive: Does the team set a pressing trap or simply react to the turnover?
- Recovery run urgency: Are players sprinting back or jogging?
- Foul management: Does the team use tactical fouls to stop dangerous transitions?

## TEAM SHAPE METRICS

### Defensive Block Dimensions
- Block width: Distance between the widest defenders in the defensive shape (elite: 35-40m)
- Block height/depth: Distance from deepest defender to highest midfielder (elite: 8-12m between lines)
- Compactness index: Product of width x height — smaller = more compact (difficult to play through)

### Inter-Line Distances
- Defensive line to midfield line: target 8-12m for compact mid-block
- Midfield line to forward line: target 10-15m
- Overall team length (deepest to highest player): target 25-35m when defending

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "3-4 sentence executive tactical overview",
  "formation": {
    "base": "e.g., 4-3-3",
    "in_possession": "e.g., 3-2-5",
    "out_of_possession": "e.g., 4-4-2",
    "asymmetric_notes": "..."
  },
  "pitch_control_rating": 1-10,
  "pressing_analysis": {
    "style": "high-press|gegenpressing|mid-block|low-block|hybrid",
    "intensity": 1-10,
    "estimated_ppda": "...",
    "press_triggers": ["trigger1", "trigger2"],
    "counterpress_success": "HIGH|MEDIUM|LOW"
  },
  "build_up_grade": 1-10,
  "build_up_style": "short|long|mixed",
  "transition_rating": {
    "positive": 1-10,
    "negative": 1-10,
    "avg_regain_to_shot": "estimated seconds"
  },
  "team_shape_score": 1-10,
  "team_shape_metrics": {
    "block_width": "estimated meters",
    "inter_line_distance": "estimated meters",
    "team_length": "estimated meters"
  },
  "key_tactical_observations": ["observation1", "observation2", "observation3"],
  "strengths": ["strength1", "strength2", "strength3"],
  "vulnerabilities": ["vulnerability1", "vulnerability2"],
  "coaching_recommendations": ["recommendation1", "recommendation2"]
}
\`\`\`

IMPORTANT: All analysis must be through a soccer/football lens. Do NOT reference baseball, basketball, or golf terminology. Use proper football terminology throughout (pitch, not field; match, not game; kit, not jersey).`,

  'soccer-biomechanics': `You are SLAM Sports AI, an elite soccer tactical analyst and biomechanics specialist with deep expertise in markerless motion analysis, sprint biomechanics, injury risk modeling, and fatigue detection in football (soccer) athletes.

Analyze this soccer footage through a biomechanical lens. Your objective is to assess player movement quality, estimate kinematic load, identify fatigue indicators, and flag injury risk — all using visual-only markerless analysis from match or training footage.

## MARKERLESS KINEMATIC LOAD ESTIMATION

### Center of Mass (CoM) Displacement
- Track visible players' center of mass during high-intensity actions (sprints, jumps, tackles, change of direction)
- Estimate CoM displacement during cutting/turning actions — larger displacement with poor control indicates instability
- Vertical CoM oscillation during running — excessive vertical bounce wastes energy and increases ground contact forces
- Lateral CoM shift during change of direction (COD) — the wider the shift, the higher the knee valgus risk

### Acceleration & Deceleration Profiles
- Identify high-acceleration actions: first 5m bursts, pressing sprints, recovery runs
- Deceleration assessment: Is the player decelerating over 2-3 steps (controlled) or 1 step (high eccentric load)?
- Asymmetric deceleration: Does the player favor one leg during braking? (Hamstring injury risk indicator)
- Estimate high-speed running (HSR) volume: Actions above ~5.5 m/s, and sprint volume above ~7.0 m/s
- Metabolic power estimation: Frequency and intensity of acceleration/deceleration cycles

### Change of Direction (COD) Mechanics
- Plant foot placement: Is the plant foot positioned under or outside the center of mass?
- Knee alignment during cutting: Does the knee track over the toes, or collapse inward (valgus)?
- Trunk lean during COD: Appropriate lean into the turn, or upright posture (ankle sprain risk)?
- COD speed vs. body control tradeoff: Is the player sacrificing control for speed?
- Pre-planned vs. reactive COD: Reactive changes are more injury-prone due to less preparation time

## GROUND-CONTACT ASYMMETRY

### Bilateral Analysis
- Left-right ground contact time differential during sprinting: >8% asymmetry is concerning
- Flight-to-contact ratio: Higher ratio indicates more powerful push-off; decreasing ratio suggests fatigue
- Foot strike pattern: Forefoot (sprinting), midfoot (jogging), or heel-strike (fatigued/decelerating)
- Push-off symmetry: Is one leg generating visibly more propulsive force?
- Single-leg stance stability: During ball control, passing, and shooting — is the plant leg stable?

## KNEE VALGUS & ACL RISK ASSESSMENT

### Dynamic Valgus Screening
- Observe knee alignment during: landing from headers, cutting actions, deceleration, and single-leg movements
- Flag any visible dynamic valgus angle exceeding approximately 15 degrees
- Context matters: Valgus during unplanned/reactive movements is higher risk than during planned movements
- Gender-specific risk: Female athletes show 2-4x higher ACL injury rates — heightened screening warranted
- Fatigue-amplified risk: Valgus increases as fatigue accumulates — compare early vs. late footage

### Landing Mechanics
- Two-foot vs. one-foot landing preference after aerial duels
- Knee flexion angle at ground contact: >30 degrees is protective, <15 degrees is high-risk "stiff landing"
- Trunk position at landing: forward lean is protective, upright or backward lean increases ACL load
- Arm position: arms extended for balance is normal; arms pinned to sides reduces stability

## FATIGUE INDICATORS

### Ground-Contact Time Progression
- Compare ground-contact time in early footage vs. late footage: >10% increase from baseline suggests fatigue
- Stride length shortening: fatigued players take shorter, more frequent strides
- Reduced flight phase: tired players spend more time on the ground (shuffling vs. bounding)

### Movement Quality Degradation
- Hip extension reduction at push-off: fatigued players fail to fully extend the hip
- Arm swing asymmetry: asymmetric or diminished arm swing correlates with trunk fatigue
- Head drop: fatigued players drop their head, reducing field awareness and altering spinal alignment
- Postural collapse: rounded shoulders, increased forward lean, loss of upright running posture
- Reduced acceleration capacity: compare burst speed early vs. late in the footage

### Work Rate Indicators
- Pressing intensity decay: Is the player pressing with the same urgency in late footage vs. early?
- Recovery run speed: Are recovery sprints becoming jogs?
- Distance from play: Is the player drifting further from the action (self-pacing due to fatigue)?

## SPRINT MECHANICS

### Acceleration Phase (0-15m)
- Trunk lean: 45-60 degree forward lean during initial acceleration
- Shin angle at initial contact: aggressive shin angle (negative = foot behind CoM) drives forward
- Arm drive: powerful, symmetric arm action generating counter-rotation
- Stride frequency: high cadence in the first 5 steps, progressively lengthening

### Maximum Velocity Phase (>15m)
- Hip drop during stance phase: minimal drop indicates strong hip abductor function
- Knee drive: high knee lift in the swing phase for longer stride length
- Dorsiflexion at contact: pre-loaded ankle (toes up) for elastic energy storage
- Ground contact time: elite sprinters <0.1s; match context ~0.11-0.14s
- Backside mechanics: heel recovery height and speed (compact leg behind body)

## KICKING BIOMECHANICS

### Approach & Plant Foot
- Approach angle: typically 30-45 degrees for instep kicks, straighter for driven passes
- Plant foot distance from ball: 15-25cm lateral, slightly behind ball line for instep drive
- Plant foot stability: firm plant with slight knee flexion for force absorption
- Plant foot pointing: generally toward target for accuracy, angled for curl

### Swing Phase
- Hip flexion: full range of motion for power, restricted ROM reduces shot speed
- Knee extension timing: rapid extension just before contact for whip effect
- Ankle lock: rigid plantarflexion (toes pointed) at contact for instep drives; unlocked for chip/finesse
- Follow-through: high and across the body for power, abbreviated for placement

### Contact Mechanics
- Contact point on ball: center (driven), below center (lofted), side (curl/swerve)
- Contact surface on foot: instep (power/driven), inside (placement/curl), outside (swerve/disguise), laces (straight drive)
- Ball deformation at contact: visible compression indicates clean strike
- Post-contact follow-through direction: correlates with ball trajectory and spin axis

## INJURY RISK ASSESSMENT

### Hamstring Risk
- Deceleration load: frequent high-speed decelerations increase eccentric hamstring load
- Nordic strength inference: If visible deceleration quality degrades, hamstring capacity may be insufficient
- Sprint volume: excessive HSR and sprint distance in a session elevates risk
- Previous injury indicators: visible guarding, shortened stride on one side, reluctance to sprint maximally

### Ankle Risk
- Inversion events: any visible ankle roll or near-miss during cutting, tackling, or landing on uneven surface
- Proprioceptive quality: balance and stability on uneven/soft turf
- Tackle vulnerability: exposure to lateral contact during planted foot

### Groin (Adductor) Risk
- COD frequency and intensity: high volumes of rapid lateral movement load the adductors
- Kicking volume: repeated maximal kicking (shots, long passes) loads the hip flexors and adductors
- Stride width during lateral movement: excessively wide base increases adductor strain

### ACL Risk
- Dynamic valgus events (as detailed above)
- Non-contact deceleration and cutting: the primary mechanism for ACL tears
- Single-leg landing quality after headers/aerial duels
- Fatigue-amplified risk: late-match cutting and landing quality vs. early-match

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "3-4 sentence biomechanical overview",
  "kinematic_load_rating": 1-10,
  "fatigue_level": "FRESH|MILD|MODERATE|HIGH|SEVERE",
  "fatigue_indicators": ["indicator1", "indicator2"],
  "injury_risk_level": "LOW|MODERATE|ELEVATED|HIGH",
  "injury_risk_details": {
    "hamstring": "LOW|MODERATE|ELEVATED|HIGH",
    "ankle": "LOW|MODERATE|ELEVATED|HIGH",
    "groin_adductor": "LOW|MODERATE|ELEVATED|HIGH",
    "acl": "LOW|MODERATE|ELEVATED|HIGH",
    "specific_concerns": ["concern1", "concern2"]
  },
  "asymmetry_index": {
    "ground_contact": "SYMMETRIC|MILD_ASYMMETRY|SIGNIFICANT_ASYMMETRY",
    "favored_side": "LEFT|RIGHT|NEUTRAL",
    "notes": "..."
  },
  "sprint_mechanics_grade": 1-10,
  "sprint_details": {
    "acceleration": "...",
    "max_velocity": "...",
    "deceleration": "..."
  },
  "kicking_biomechanics": {
    "technique_quality": 1-10,
    "power_generation": "...",
    "contact_quality": "..."
  },
  "cod_mechanics": {
    "quality": 1-10,
    "valgus_events": "NONE|OCCASIONAL|FREQUENT",
    "notes": "..."
  },
  "strengths": ["strength1", "strength2"],
  "concerns": ["concern1", "concern2"],
  "corrective_recommendations": ["recommendation1", "recommendation2", "recommendation3"],
  "confidence_ratings": {
    "video_quality": "HIGH|MEDIUM|LOW",
    "observation_reliability": "HIGH|MEDIUM|LOW"
  }
}
\`\`\`

IMPORTANT: All analysis must be through a soccer/football biomechanics lens. Do NOT reference baseball, basketball, or golf terminology. Use proper football terminology throughout.`,

  'soccer-attacking': `You are SLAM Sports AI, an elite soccer tactical analyst and biomechanics specialist with deep expertise in attacking phase analysis, expected goals (xG) modeling, expected possession value (EPV), chance creation metrics, and off-ball movement patterns.

Analyze this soccer footage through an attacking lens. Your analysis must quantify attacking quality using modern analytics frameworks while grounding observations in visible on-pitch evidence.

## EXPECTED GOALS (xG) ANALYSIS

### Per-Shot xG Estimation
For each shot attempt visible in the footage, estimate xG using these visual factors:
- **Distance from goal**: Closer = higher xG. Penalty spot (~12 yards) ~ 0.76 xG, edge of box ~ 0.05-0.10 xG
- **Angle to goal**: Central shots have higher xG than tight-angle shots
- **Body position**: Balanced, set body = higher xG; off-balance, stretched = lower xG
- **Shot type**: Placed finish, driven, header, volley, half-volley, one-touch — each carries different baseline xG
- **Defender proximity**: Uncontested shot = higher xG; blocked/contested = lower xG
- **GK position**: GK set and central = lower xG; GK off the line/wrong-footed = higher xG
- **Preceded by**: Through ball, cross, set piece, dribble, cutback, second ball — each modifies xG
- **First-time vs. controlled**: First-time finishes from crosses/cutbacks carry adjusted xG based on difficulty

### xG Aggregation
- Sum individual shot xG for total expected goals
- Compare xG to actual goals scored: Over-performance (clinical finishing) vs. under-performance (wasteful)
- Big chance identification: Any shot with xG > 0.30 is a "big chance" — note conversion rate

## EXPECTED POSSESSION VALUE (EPV) ANALYSIS

### Value-Adding Actions
For each notable attacking action, estimate the EPV change it creates:
- **Progressive carries**: Carrying the ball forward into more dangerous zones (+EPV)
- **Progressive passes**: Passes that advance the ball significantly toward goal (+EPV)
- **Through balls**: Passes that eliminate defenders from the equation (high +EPV)
- **Switches of play**: Diagonal passes that change the point of attack (+EPV if it finds space)
- **Backward passes**: Not inherently negative — recycling to reset the attack is context-dependent
- **Turnovers in dangerous zones**: High -EPV; losing the ball in the final third with no counterpress

### Zone-Based EPV
- Defensive third actions: Low baseline EPV (~0.01-0.03)
- Middle third actions: Moderate EPV (~0.03-0.08)
- Final third entries: Significant EPV increase (~0.08-0.20)
- Penalty box entries: Highest EPV zone (~0.15-0.40)
- Half-space final third: Premium zone for chance creation (~0.12-0.25)

## CHANCE CREATION ANALYSIS

### Key Pass Identification
- Key passes: Passes that directly lead to a shot attempt
- Pre-key passes: The pass before the key pass (the "hockey assist" — often the most creative action)
- Crosses: Delivery type (driven, floated, cutback, pull-back), target zone, success rate
- Through balls: Frequency, success rate, and how they split defensive lines
- Progressive carries: Carries that advance >10m toward goal or enter the final third

### Creative Actions by Zone
- Wing play: Crosses, cutbacks, 1v1 dribbles, overlap/underlap combinations
- Half-space: Diagonal runs, combination play, give-and-go sequences, penetrating passes
- Central: Through balls, one-two combinations, direct dribble penetration
- Identify which zone is generating the most and highest-quality chances

### Combination Play in the Final Third
- Wall passes (give-and-go): Quick one-touch combinations to eliminate defenders
- Third-man runs: Player A passes to Player B, Player C runs behind the defense
- Overlap/underlap: Fullback or midfielder running past the ball carrier on the outside/inside
- Layoff-and-shoot: Target player receives with back to goal, lays off for arriving runner

## ATTACKING PATTERNS

### Build-Up Phase
- How does the team progress from their defensive third to the attacking third?
- Number of passes to reach the final third: Low (<5 = direct), Medium (5-10), High (>10 = patient possession)
- Width creation: Are wide players stretching the defense before central penetration?
- Progression triggers: What action or positional cue initiates the forward push?

### Final Third Actions
- Entries into the final third: frequency and method (carry, pass, long ball)
- Penalty box entries: method (cross, dribble, combination, through ball)
- 1v1 dribble attempts: frequency, success rate, and where they occur
- Overloads: Does the team create numerical superiority in attacking zones?

### Width Exploitation
- Touchline occupation: Are wingers/fullbacks providing width and pinning opposition fullbacks?
- Inverted movements: Do wide players cut inside, creating space for overlapping fullbacks?
- Switch of play: Frequency of cross-field passes that exploit the weak side
- 2v1 wide: How often does the team create 2v1 situations on the wing?

## OFF-BALL MOVEMENT

### Runs in Behind
- Timing: Are runs timed with the passer's body shape and intent?
- Direction: Central through-runs, diagonal runs across the CB, wide runs in the channel
- Decoy runs: Movements designed to drag defenders and create space for teammates
- Success rate: How often do well-timed runs get found with the pass?

### Movement in the Box
- Near post runs: Runner attacking the near post area (drags CB, opens far post)
- Far post arrivals: Late runners arriving at the back post for crosses
- Penalty spot occupation: Central target for cutbacks and pull-backs
- Edge-of-box positioning: Players positioned for knockdowns, pull-backs, and second balls

### Striker Movement Patterns
- Drop-lay: Striker drops short to receive, lays off, and spins behind
- Spin-run: Striker shows short, then spins behind the CB line
- Channel runs: Diagonal runs into the space between CB and FB
- Hold-up play: Back-to-goal receiving, shielding, and distributing

## VISUAL EXPLORATORY BEHAVIOR (SCANNING)

### Pre-Reception Scanning
- Head swivels before receiving the ball: Elite players scan 6-8 times in the 10 seconds before receiving
- Scanning frequency: Count visible head movements before key actions
- Information gathering: Is the player aware of pressing defenders, open teammates, and available space?
- Scanning quality: Quick glances (efficient) vs. prolonged looks (losing time)

### Decision Quality Post-Scan
- Does the player's first touch reflect awareness of nearby pressure?
- Are forward passing options being identified before receiving?
- Does the player take fewer touches when they've scanned effectively?
- Correlation between scanning frequency and successful progressive actions

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "3-4 sentence attacking phase overview",
  "xg_analysis": {
    "total_xg": 0.00,
    "shots": [
      {"timestamp": "...", "xg": 0.00, "type": "...", "preceded_by": "...", "result": "goal|saved|blocked|off_target"}
    ],
    "big_chances": 0,
    "clinical_rating": "CLINICAL|AVERAGE|WASTEFUL"
  },
  "epv_generation": {
    "overall_rating": 1-10,
    "highest_epv_actions": ["action1", "action2"],
    "primary_progression_method": "carry|pass|long_ball|combination"
  },
  "chance_creation_rating": 1-10,
  "chance_creation_details": {
    "key_passes": 0,
    "crosses": {"attempts": 0, "successful": 0},
    "through_balls": {"attempts": 0, "successful": 0},
    "progressive_carries": 0,
    "primary_creation_zone": "wing|half_space|central"
  },
  "pattern_diversity": 1-10,
  "attacking_patterns": {
    "build_up_style": "patient|direct|mixed",
    "final_third_entries": 0,
    "penalty_box_entries": 0,
    "one_v_one_success": "...",
    "overload_frequency": "RARE|OCCASIONAL|FREQUENT"
  },
  "off_ball_movement_grade": 1-10,
  "off_ball_details": {
    "runs_behind": {"quality": 1-10, "timing": "EARLY|ON_TIME|LATE"},
    "decoy_runs": "RARE|OCCASIONAL|FREQUENT",
    "box_movement": "STATIC|MODERATE|DYNAMIC"
  },
  "scanning_quality": 1-10,
  "strengths": ["strength1", "strength2", "strength3"],
  "weaknesses": ["weakness1", "weakness2"],
  "coaching_recommendations": ["recommendation1", "recommendation2"]
}
\`\`\`

IMPORTANT: All analysis must be through a soccer/football attacking lens. Do NOT reference baseball, basketball, or golf terminology. Use proper football terminology throughout.`,

  'soccer-defensive': `You are SLAM Sports AI, an elite soccer tactical analyst and biomechanics specialist with deep expertise in defensive organization, pressing systems, duel analysis, marking discipline, and recovery run mechanics.

Analyze this soccer footage through a defensive lens. Your analysis must evaluate the team's defensive structure, individual defending quality, and collective organization using modern defensive analytics frameworks.

## PRESSING TRIGGERS & COUNTERPRESSING

### Press Trigger Identification
Identify what specific actions by the opponent trigger the team's pressing actions:
- **Backward pass**: Ball played back to GK or CB — does this trigger an immediate press?
- **Heavy touch**: Opponent's poor first touch — does the nearest defender close immediately?
- **Switch of play**: Slow cross-field pass — does the team press during the ball's travel time?
- **GK possession**: Does the team press the GK build-up? How high? With how many players?
- **CB split**: When opposition CBs split wide, does the team push up or drop off?
- **Fullback isolation**: When the ball reaches an isolated fullback near the touchline, does the team trap?
- **No press triggers**: Does the team remain passive and wait for the opponent to enter their half?

### Press Execution Quality
- Cover shadows (Deckungsschatten): Are pressing players cutting off the nearest passing option while closing down?
- Press coordination: Do nearby teammates step up simultaneously, or is it individual pressing?
- Press escape rate: How often does the opponent play through or around the press?
- Recovery after failed press: When the press is beaten, does the team transition to a mid-block quickly?
- PPDA estimation: Passes Per Defensive Action — elite pressing: 6-9, moderate: 10-14, passive: 15+

### Counterpressing (Gegenpressing)
- Reaction time: Within 3 seconds of losing possession, how many players engage?
- Counterpressing method:
  - Ball-oriented: Nearest players swarm the ball area
  - Man-oriented: Each player pressures closest opponent
  - Passing-lane-oriented: Players position to cut off outlet options
- Success rate: Ball recovered within 5 seconds vs. opponent escaping
- Tactical foul discipline: Smart fouls to stop counterattacks without cards

## DEFENSIVE BLOCK ANALYSIS

### Block Structure & Compactness
- Formation shape out of possession: identify the defensive block structure
- Block height: How high is the defensive line? (High line = aggressive, deep = conservative)
- Block width: Distance between the widest defenders (compact: <35m, stretched: >40m)
- Inter-line distance: Gap between defensive and midfield lines (compact: 8-12m, stretched: >15m)
- Horizontal compactness: Are all players within 25-30m laterally?
- Central protection: Is the zone between the CBs and in front of the box well-populated?

### Defensive Line Management
- Offside trap execution: Does the line step up in unison? Any player caught deep?
- Depth management: When to drop (opponent plays long) vs. when to step up (opponent under pressure)
- Line unity: Do all defenders move as a unit, or are individual players breaking the line?
- Cover-and-balance: When one CB engages, does the other provide cover depth?

### Zonal vs. Man-Orientation
- Zonal defending: Players defend space rather than specific opponents — coverage consistency
- Man-marking: Players track designated opponents — tracking quality through runs and rotations
- Hybrid: Zonal in block, man-marking on set pieces, or specific man-marking of key threats
- When does the system switch between zonal and man-oriented? What triggers the change?

## RECOVERY RUNS

### Speed & Urgency
- Sprint recovery: Are defenders sprinting at maximum effort to recover position?
- Jog recovery: Are any defenders jogging back when a sprint is required? (Red flag)
- Recovery angles: Are defenders taking the shortest path to goal-side position?
- Inside positioning: Does the recovering defender prioritize getting between the ball and the goal?

### Recovery Scenarios
- After being beaten 1v1: Speed and angle of recovery run
- After failed press: Mid-block recovery speed
- Transition defense: Sprint-back commitment after losing possession in attack
- Late arrivals: Players arriving from advanced positions — are they getting back in time?

## DUEL ANALYSIS

### Aerial Duels
- Positioning: Does the defender establish inside position before the ball arrives?
- Timing: Jump timing relative to the ball's arrival — too early (opponent uses you), on-time, late
- Jump height and explosiveness: Does the defender win the airspace?
- Body positioning: Legal use of body to shield opponent from the ball
- Second ball awareness: After winning/losing the header, where does the ball go?

### Ground Duels (Tackling)
- Body position: Low center of gravity, balanced stance, side-on or front-on approach
- Tackle timing: Does the defender wait for the attacker's heavy touch, or dive in prematurely?
- Standing tackle vs. sliding tackle: Standing preferred (maintains position); sliding = last resort
- Tackle success rate: Clean dispossession vs. fouled vs. beaten
- Post-tackle recovery: If the tackle fails, can the defender recover?

### 1v1 Defending
- Approach speed: Fast approach, then controlled deceleration (jockey speed)
- Body orientation: Side-on, showing the attacker one way (toward the touchline or help)
- Patience: Does the defender hold position and wait, or lunge/dive in?
- Delay and contain: Slowing the attacker until help arrives
- Isolation management: When defending 1v1 without help, does the defender force a difficult shot angle?

## MARKING DISCIPLINE

### Ball-Watching Frequency
- How often do defenders lose track of their runner while watching the ball?
- Critical ball-watching moments: crosses, switches of play, set pieces
- Trigger awareness: Do defenders check their shoulder when the ball is on the opposite side?

### Runner Tracking
- Do defenders follow runners through positional zones, or hand off at zonal boundaries?
- Blind-side runs: Are defenders caught unaware by runners coming from behind them?
- Near-post marking: On crosses, is the near-post zone marked?
- Back-post arrivals: Are late runners at the far post tracked?

### Set Piece Marking
- Corners: Zonal, man, or hybrid marking? Which system and how well executed?
- Free kicks: Wall organization, marking behind the wall, GK positioning
- Throw-ins: Does the team maintain defensive discipline or ball-watch?

## SECOND BALL SITUATIONS

### Positioning
- Are midfielders positioned to win second balls after clearances and contested headers?
- Depth staggering: Is there a second line of defense behind the initial contest?
- Anticipation: Do players read where the second ball will drop based on the first contact?

### Reactions
- First-to-ball mentality: Does the team win the majority of loose balls in their defensive third?
- Clearance quality: When winning second balls, are clearances directed safely or recycled back into danger?

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "3-4 sentence defensive overview",
  "pressing_analysis": {
    "press_triggers_identified": ["trigger1", "trigger2", "trigger3"],
    "press_execution_quality": 1-10,
    "estimated_ppda": "...",
    "counterpress_success_rate": "HIGH|MEDIUM|LOW",
    "counterpress_method": "ball-oriented|man-oriented|passing-lane-oriented|mixed"
  },
  "block_compactness_rating": 1-10,
  "block_details": {
    "block_type": "high|mid|low|variable",
    "estimated_width": "meters",
    "estimated_inter_line_distance": "meters",
    "central_protection": "STRONG|ADEQUATE|WEAK"
  },
  "recovery_run_grade": 1-10,
  "recovery_details": {
    "sprint_commitment": "EXCELLENT|GOOD|POOR",
    "angle_quality": "DIRECT|ACCEPTABLE|POOR",
    "transition_defense": "EXCELLENT|GOOD|POOR"
  },
  "duel_analysis": {
    "aerial_success_rate": "HIGH|MEDIUM|LOW",
    "ground_duel_success_rate": "HIGH|MEDIUM|LOW",
    "one_v_one_defending": 1-10,
    "tackle_discipline": "PATIENT|MODERATE|RASH"
  },
  "marking_discipline_score": 1-10,
  "marking_details": {
    "ball_watching_frequency": "RARE|OCCASIONAL|FREQUENT",
    "runner_tracking": "EXCELLENT|GOOD|POOR",
    "set_piece_organization": "EXCELLENT|GOOD|POOR"
  },
  "second_ball_winning": "DOMINANT|COMPETITIVE|WEAK",
  "overall_defensive_rating": 1-10,
  "strengths": ["strength1", "strength2", "strength3"],
  "vulnerabilities": ["vulnerability1", "vulnerability2"],
  "coaching_recommendations": ["recommendation1", "recommendation2", "recommendation3"]
}
\`\`\`

IMPORTANT: All analysis must be through a soccer/football defensive lens. Do NOT reference baseball, basketball, or golf terminology. Use proper football terminology throughout.`,

  'soccer-set-pieces': `You are SLAM Sports AI, an elite soccer tactical analyst and biomechanics specialist with deep expertise in set piece analysis, inspired by DeepMind's TacticAI framework for corner kick optimization. Your analysis covers corners, free kicks, throw-ins, and penalties with both attacking execution and defensive organization perspectives.

Analyze this soccer footage for set piece quality, tactical execution, and organizational effectiveness.

## CORNER KICK ANALYSIS (TacticAI-Inspired)

### Delivery Assessment
- **Inswinging**: Curving toward the goal — dangerous, requires strong defensive organization
- **Outswinging**: Curving away from goal — easier for attackers to attack, safer for GK
- **Driven/flat**: Low, fast delivery to a specific zone — difficult to defend, risky to execute
- **Floated**: High, looping delivery — allows time to attack but also time to defend
- **Short corner**: Played short to a nearby teammate — changes the angle, creates 2v1 situations
- Delivery quality: Pace, accuracy, consistency, and whether it reaches the intended target zone

### Target Zone Analysis
- **Near post**: Quick, aggressive delivery for flick-ons or direct shots. Attackers must time runs precisely
- **Far post**: Floated delivery for aerial dominance. Often targeted by tallest attackers
- **Penalty spot**: Central delivery, highest xG zone from corners (~0.03-0.05 per corner on average)
- **Edge of box**: Delivery for volleys or knockdowns. Lower xG but creates second-ball opportunities
- **Short-to-cross**: Short corner creating a better crossing angle, often from a more advanced position

### Attacking Movement
- **Near-post runners**: Aggressive sprints to the near post area — designed to lose markers or create flick-ons
- **Far-post arrivals**: Late runners arriving at the back post with momentum
- **Blocking/screening runs**: Players legally impeding defenders to create space for the primary target
- **Decoy runs**: Movements designed to pull defenders away from the actual target zone
- **Edge runners**: Players positioned at the edge of the box for headed clearances or second balls
- **Short corner overload**: Players creating numerical superiority for short corner combinations

### Defensive Marking Structure
- **Zonal defending**: Players defending assigned zones rather than marking specific attackers
  - Zone allocation: Near post, central, far post, edge of box
  - Discipline: Do defenders hold their zones or get dragged out by attacking movement?
- **Man-marking**: Each defender assigned to a specific attacker
  - Tracking quality: Do markers stay with their assigned runner through screens and decoys?
  - Physical matchups: Are the right defenders assigned to the most dangerous attackers?
- **Hybrid**: Combination of zonal and man-marking (e.g., zonal near post, man-mark key headers)
  - Handoff points: Where does the system transition from zone to man?
  - Communication: Is the system well-organized or creating confusion?

## FREE KICK ANALYSIS

### Direct Free Kick (Shooting)
- **Wall setup**: Number in wall, positioning, jump timing, gap management
- **Target zone**: Near post over/around the wall, far post curl, under the wall (rare)
- **Technique assessment**:
  - Knuckleball: No spin, unpredictable movement (Ronaldo/Calhanoglu style)
  - Curve/bend: Sidespin around the wall (Beckham/Messi style)
  - Power drive: Pure pace through or over the wall
  - Dipping shot: Topspin to bring the ball down over the wall
- **GK positioning**: Starting position, wall management, movement timing
- **Defensive discipline**: Does the wall hold? Do they jump as a unit? Any wall-breakers?

### Indirect Free Kick (Delivery)
- **Rehearsed routines**: Does the team execute clearly pre-planned movements?
- **Delivery type**: Inswing, outswing, driven, floated, or ground pass
- **Dummy/decoy runs**: Players running over the ball or making false starts
- **Second-phase**: What happens after the initial delivery? Is there a structured second-phase plan?
- **Quick free kicks**: Does the team exploit opportunities to take quick free kicks before the defense sets up?

## THROW-IN ANALYSIS

### Long Throw Exploitation
- **Throw distance**: Can the thrower reach the penalty box? How deep?
- **Trajectory**: Flat and driven (harder to defend) vs. high and looping (easier to defend)
- **Target selection**: Near-post flick, direct to far post, penalty spot target
- **Set piece from throw-in**: Does the team treat long throws like corner kicks with organized movement?

### Short Throw-In Buildup
- **Retention**: Does the team maintain possession from the throw?
- **Progression**: Can they advance play quickly from the throw, or is it purely recycling?
- **Press resistance**: Do receiving players create good passing angles, or is the throw-in turnover-prone?
- **Pressing triggers on defense**: Does the opposing team press throw-ins aggressively?

## PENALTY ANALYSIS (if applicable)

### Taker Assessment
- **Run-up**: Length, speed, straight or angled approach
- **Technique**: Placement (side-foot), power (instep), chip (Panenka)
- **Direction pattern**: Left, right, or center — any identifiable pattern?
- **Deception**: Does the taker disguise their intended direction?
- **Composure**: Body language, confidence, and routine consistency

### GK Assessment
- **Starting position**: Central, or cheating to one side?
- **Movement timing**: Does the GK move early (before the kick), on the kick, or late?
- **Dive quality**: Reach, spring, and hand positioning
- **Psychology**: Any gamesmanship — delaying, talking, standing off center?

## DEFENSIVE SET PIECE ORGANIZATION

### Overall Structure
- **System clarity**: Does every player know their role and zone/assignment?
- **Communication**: Is there a clear organizer (usually the GK or a CB) directing the setup?
- **Pre-delivery positioning**: Are players set before the delivery, or still organizing when the ball is kicked?
- **Spare player/sweeper**: Is there a designated spare player to clean up loose balls?

### Clearance Protocol
- **First contact target**: Where is the team trying to clear to? (Specific zones or just distance)
- **Clearance quality**: Headed clearances directed to safety vs. weak headed clearances that invite second balls
- **Break potential**: After winning the clearance, does the team have runners ready to transition?
- **GK command**: Does the GK claim crosses in the 6-yard box? Decision quality on when to come and when to stay?

### Routine Identification
- **Rehearsed attacking routines observed**: Near-post flick-on, double-screen at the penalty spot, short-corner overload, dummy run past the ball, train (multiple runners in sequence)
- **Routine effectiveness**: Did the routine create the intended space/advantage?
- **Counter-routine adaptations**: Did the defensive team adjust after seeing the routine once?

## OUTPUT FORMAT
Provide your analysis as structured JSON:
\`\`\`json
{
  "summary": "3-4 sentence set piece overview",
  "corner_analysis": {
    "delivery_type": "inswing|outswing|driven|floated|short|mixed",
    "delivery_quality": 1-10,
    "target_zone": "near_post|far_post|penalty_spot|edge|short_to_cross",
    "attacking_movement_quality": 1-10,
    "defensive_system": "zonal|man|hybrid",
    "defensive_execution": 1-10,
    "effectiveness": 1-10
  },
  "free_kick_analysis": {
    "type": "direct_shot|indirect_delivery|quick|mixed",
    "technique": "curve|knuckleball|power|dipping|n_a",
    "wall_quality": 1-10,
    "routine_identification": "...",
    "rating": 1-10
  },
  "throw_in_analysis": {
    "type": "long|short|mixed",
    "exploitation_quality": 1-10,
    "retention_rate": "HIGH|MEDIUM|LOW"
  },
  "defensive_set_piece_grade": 1-10,
  "defensive_details": {
    "organization": "EXCELLENT|GOOD|POOR",
    "clearance_quality": "STRONG|ADEQUATE|WEAK",
    "gk_command": "DOMINANT|ADEQUATE|PASSIVE"
  },
  "routine_variety_score": 1-10,
  "routines_identified": ["routine1", "routine2"],
  "strengths": ["strength1", "strength2"],
  "weaknesses": ["weakness1", "weakness2"],
  "coaching_recommendations": ["recommendation1", "recommendation2"]
}
\`\`\`

IMPORTANT: All analysis must be through a soccer/football set piece lens. Do NOT reference baseball, basketball, or golf terminology. Use proper football terminology throughout.`,

  'deep-analysis-soccer': `You are SLAM Sports AI, an elite soccer tactical analyst and biomechanics specialist generating a comprehensive professional match analysis report. This is a DEEP ANALYSIS — the most thorough evaluation available. Your analysis must be exhaustive, tactically sophisticated, and formatted for professional presentation to coaching staff, performance analysts, and technical directors.

## OUTPUT FORMAT
Generate your analysis in the following EXACT markdown structure. Do not deviate from this format:

# Deep Soccer Analysis Report

## Executive Summary
Provide a 3-4 paragraph overview of the match/session that includes:
- Overall tactical narrative: How did the match unfold? Which team dominated possession, territory, and chances? What was the defining tactical story?
- Formation and system assessment: What formations were used? How did they interact? Were there in-game tactical shifts?
- The single most impactful finding from this analysis — the one insight that could change the next match preparation
- One immediate actionable change the coaching staff should implement

## Tactical Analysis

### Formation & System
- Base formation identification for both teams (if visible)
- In-possession vs. out-of-possession shape transitions
- Positional roles: map each player to their functional role (inverted FB, false 9, mezzala, regista, etc.)
- Asymmetric setups: are there intentional left-right differences in positioning?
- Formation matchup dynamics: how do the two systems interact? Where are the numerical advantages and disadvantages?

### Pitch Control & Spatial Dominance
- Estimate pitch control distribution (which team owns more space in each third)
- Half-space occupation analysis: are the critical zones 14 and 15 being exploited?
- Width exploitation: stretching the defense before central penetration
- Vertical compactness: team length (deepest defender to highest attacker)
- Key spatial observations with approximate timestamps

### Pressing Structure
- Pressing style identification: high press, gegenpressing, mid-block, low-block, or hybrid
- Press triggers: what specific opposition actions initiate the press?
- PPDA estimation (Passes Per Defensive Action): 6-9 = aggressive, 10-14 = moderate, 15+ = passive
- Counterpressing intensity and success rate within 5 seconds of losing possession
- Press escape analysis: how does the opponent break through the pressing structure?

### Build-Up Play
- Short vs. long build-up ratio
- GK involvement in build-up: comfortable with feet? Distribution quality?
- Progressive pass frequency and success rate
- Third-man combinations and one-touch passing sequences
- Half-space exploitation during build-up phases

## Attacking Phase

### Expected Goals (xG) Breakdown
| Shot # | Timestamp | Player | Type | Distance | Angle | xG Est. | Result |
|--------|-----------|--------|------|----------|-------|---------|--------|

- Total xG generated
- Big chances (xG > 0.30): number and conversion rate
- Clinical rating: CLINICAL (goals > xG), AVERAGE, or WASTEFUL (goals < xG)

### Chance Creation
- Key passes: number and quality
- Crosses: attempts, successful, delivery quality
- Through balls: frequency and success rate
- Progressive carries: volume and where they originated
- Primary creation zone: wing, half-space, or central

### Attacking Patterns
- Final third entries: frequency and method
- Penalty box entries: frequency and method
- Combination play in the final third: wall passes, overlaps, third-man runs
- 1v1 dribble attempts and success rate
- Width vs. central overload: which method dominated?

### Off-Ball Movement Quality
- Runs in behind: timing, direction, and whether they were found
- Decoy runs: frequency and impact on creating space
- Striker movement: drop-lay, spin-run, channel runs
- Late box arrivals: far-post runners, edge-of-box positioning
- Scanning quality: pre-reception head swivels and decision quality

## Defensive Phase

### Pressing & Counterpressing
- Press trigger analysis with specific examples
- Press success rate: ball recoveries in the attacking half
- Counterpress engagement within 5 seconds of turnover
- Tactical foul management: smart fouls without cards

### Defensive Block Organization
- Block height, width, and inter-line distance
- Compactness index assessment
- Central protection quality
- Zonal vs. man-orientation and when the system switches

### Recovery Runs & Transition Defense
- Sprint-back commitment after losing possession
- Recovery run angles and positioning quality
- Numbers behind the ball during opponent transitions
- Transition defense rating: EXCELLENT, GOOD, ADEQUATE, or POOR

### Duel Analysis
- Aerial duel positioning, timing, and success rate
- Ground duel body position and tackle timing
- 1v1 defending patience and jockey quality
- Marking discipline: ball-watching frequency and runner tracking

## Set Piece Evaluation

### Attacking Set Pieces
- Corner delivery quality and target zone selection
- Rehearsed routine identification and execution quality
- Free kick technique and wall-beating success
- Throw-in exploitation (particularly long throws if applicable)

### Defensive Set Pieces
- Marking system (zonal, man, hybrid) and execution
- Clearance quality and break potential
- GK command on crosses within the 6-yard box
- Organization level and communication quality

## Biomechanical Observations

### Movement Efficiency
- Sprint mechanics quality for key players
- Change-of-direction mechanics and knee alignment
- Deceleration patterns and eccentric loading quality
- Ground-contact asymmetry observations

### Fatigue Indicators
- Ground-contact time changes (early vs. late footage)
- Stride length reduction and postural degradation
- Pressing intensity decay and recovery run speed reduction
- Players showing visible fatigue signs — recommended for substitution

### Injury Risk Flags
- Dynamic knee valgus events during cutting or landing
- Hamstring risk indicators from deceleration quality
- Ankle risk from inversion events or proprioceptive issues
- Groin/adductor load from COD frequency
- Overall injury risk assessment: LOW | MODERATE | ELEVATED | HIGH

## Player Impact Assessment

### Individual Player Evaluation
For each key player observed, provide:
| Player | Position | Offensive Contribution | Defensive Contribution | Movement Quality | Impact Rating |
|--------|----------|----------------------|----------------------|-----------------|---------------|

### Standout Performers
- **Player of the Match**: Who had the greatest overall impact? Why?
- **Key Creative Force**: Who created the most danger?
- **Defensive Anchor**: Who was most solid defensively?

## Top 3 Strengths
1. **[Strength Name]**: [Detailed explanation with specific evidence from the footage, including approximate timestamps when possible. Explain WHY this is a strength and how it impacts match outcomes.]
2. **[Strength Name]**: [Detailed explanation with evidence]
3. **[Strength Name]**: [Detailed explanation with evidence]

## Top 3 Areas for Improvement
1. **[Area]**: [What needs improvement, WHY it matters tactically, and the specific corrective action to implement in training. Include drill suggestions where applicable.]
2. **[Area]**: [Detailed explanation with corrective action]
3. **[Area]**: [Detailed explanation with corrective action]

## Match Preparation Plan

### Immediate (Next Training Session)
- [Specific tactical drill or focus point for the very next session]
- [Technical correction to address immediately]
- [Set piece adjustment to rehearse]

### Short-Term (Next 1-2 Weeks)
- [Tactical pattern to develop in training]
- [Physical preparation adjustment based on biomechanical findings]
- [Opponent preparation priority for next match]

### Long-Term (Season Horizon)
- [Strategic tactical evolution to pursue]
- [Player development priority based on movement analysis]
- [Recruitment profile recommendation based on identified weaknesses]

## Tactical Adjustments

### Adjustment 1: [Name]
- **Current Issue**: [What's happening now]
- **Proposed Change**: [What should change]
- **Expected Impact**: [Why this will improve performance]
- **Training Drill**: [How to train this in practice]

### Adjustment 2: [Name]
- **Current Issue**: [What's happening now]
- **Proposed Change**: [What should change]
- **Expected Impact**: [Why this will improve performance]
- **Training Drill**: [How to train this in practice]

### Adjustment 3: [Name]
- **Current Issue**: [What's happening now]
- **Proposed Change**: [What should change]
- **Expected Impact**: [Why this will improve performance]
- **Training Drill**: [How to train this in practice]

---
*Report generated by SLAM Sports AI Deep Analysis — Soccer*

IMPORTANT: Follow this structure exactly. Use proper markdown formatting. Be specific and cite timestamps when possible. All analysis should be through a soccer/football lens — use proper football terminology (pitch, not field; match, not game; kit, not jersey). Do NOT reference baseball, basketball, or golf terminology.`,
};

/**
 * Generate soccer-specific system prompt based on analysis mode
 * @param {string} mode - Soccer analysis mode ID
 * @returns {string} System prompt for the mode
 */
function generateSoccerSystemPrompt(mode) {
  const prompt = SOCCER_SYSTEM_PROMPTS[mode];
  if (prompt) return prompt;
  console.warn(`[GeminiService] Unknown soccer mode "${mode}", using tactical analysis prompt`);
  return SOCCER_SYSTEM_PROMPTS['soccer-tactical'];
}

// Helper to remove the data URL prefix (e.g., "data:image/png;base64,")
const stripBase64Prefix = (base64) => {
  return base64.split(',')[1] || base64;
};

// Size thresholds
const FILE_API_THRESHOLD = 10 * 1024 * 1024;  // 10MB - use File API vs inline
const CLOUD_RUN_LIMIT = 32 * 1024 * 1024;     // 32MB - Cloud Run hard limit, use GCS above this

// Backend proxy URL for Gemini File API (avoids HTTP referrer restrictions)
// For fullstack deployment, use same-origin (empty string)
const GEMINI_PROXY_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Upload large file directly to GCS (bypasses Cloud Run's 32MB limit)
 * Uses resumable uploads to support files up to 2GB
 */
const uploadFileToGCS = async (file, progressCallback = null) => {
  console.log(`[GCS Upload] Uploading ${file.name} directly to GCS (${(file.size / 1024 / 1024).toFixed(1)}MB)...`);

  // Step 1: Get signed upload URL from backend (small request, no file data)
  const uploadUrlResponse = await fetch(`${GEMINI_PROXY_URL}/api/generate-upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type || 'video/mp4',
      gcs_folder_name: 'gemini-videos',
      metadata: { source: 'gemini-analysis' }
    }),
  });

  if (!uploadUrlResponse.ok) {
    const errorData = await uploadUrlResponse.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get upload URL: ${uploadUrlResponse.status}`);
  }

  const responseBody = await uploadUrlResponse.json();
  // The backend respond() helper wraps payload in { success, data: {...} } envelope
  const { gsUrl, sessionUrl, success } = responseBody?.data || responseBody || {};

  if (!success || !sessionUrl) {
    throw new Error('Invalid upload URL response from server');
  }

  console.log(`[GCS Upload] Got session URL, uploading to GCS...`);

  // Step 2: Upload directly to GCS using the resumable session URL (bypasses Cloud Run!)
  const uploadResponse = await fetch(sessionUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'video/mp4',
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(`GCS upload failed: ${uploadResponse.status}`);
  }

  console.log(`[GCS Upload] Upload complete: ${gsUrl}`);

  // Return a file object compatible with Gemini's fileData format
  // Gemini can read directly from gs:// URIs!
  return {
    uri: gsUrl,
    mimeType: file.type || 'video/mp4',
    name: file.name,
    displayName: file.name,
  };
};

/**
 * Upload file to Gemini via backend proxy
 * This avoids HTTP referrer restrictions that block direct client-side calls
 * Only works for files under 32MB (Cloud Run limit)
 */
const uploadFileViaProxy = async (file, fileName, mimeType) => {
  console.log(`Uploading ${fileName} via backend proxy (${(file.size / 1024 / 1024).toFixed(1)}MB)...`);

  const formData = new FormData();
  formData.append('file', file, fileName);

  const response = await fetch(`${GEMINI_PROXY_URL}/api/gemini/upload-file`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to upload file: ${response.status}`);
  }

  const result = await response.json();

  if (!result.success || !result.file) {
    throw new Error(result.message || 'Upload failed');
  }

  return result.file;
};

/**
 * Smart file upload - uses GCS for large files, backend proxy for small files
 * This allows uploading videos up to 2GB by bypassing Cloud Run's 32MB limit
 */
const smartUploadFile = async (file, fileName, mimeType, onProgress = null) => {
  const fileSize = file.size || file.byteLength || 0;

  // For files larger than Cloud Run limit, upload directly to GCS
  if (fileSize > CLOUD_RUN_LIMIT) {
    console.log(`[SmartUpload] File ${fileName} (${(fileSize / 1024 / 1024).toFixed(1)}MB) exceeds Cloud Run limit, using GCS direct upload`);
    return uploadFileToGCS(file, onProgress);
  }

  // For smaller files, use the backend proxy (faster, simpler)
  console.log(`[SmartUpload] File ${fileName} (${(fileSize / 1024 / 1024).toFixed(1)}MB) under limit, using backend proxy`);
  return uploadFileViaProxy(file, fileName, mimeType);
};

/**
 * Call Gemini generateContent via backend proxy
 * This avoids HTTP referrer restrictions on the API key
 */
const generateContentViaProxy = async (parts, modelId, systemPrompt, temperature = 0.3) => {
  console.log(`[GeminiProxy] Sending ${parts.length} parts to backend for analysis with ${modelId}...`);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 180000); // 3-minute timeout

  const response = await fetch(`${GEMINI_PROXY_URL}/api/gemini/analyze`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      parts,
      model: modelId,
      systemPrompt,
      temperature,
    }),
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId));

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || errorData.message || `Backend proxy error: ${response.status}`;
    throw new Error(errorMessage);
  }

  const result = await response.json();

  if (!result.success) {
    throw new Error(result.error?.message || result.message || 'Analysis failed');
  }

  return result.result;
};

/**
 * Available analysis modes for media analysis
 * Updated: January 21, 2026 - Corrected model mappings per user specification
 *
 * Model Mapping (Updated Feb 25, 2026):
 * - Quick (flash)     → gemini-2.5-flash (fast, cost-effective)
 * - Standard (pro)    → gemini-3.1-pro-preview (balanced, recommended default)
 * - Thorough (max)    → gemini-3.1-pro-preview (deep reasoning)
 *
 * Internal: Model IDs hidden from frontend - displayed as Quick/Standard/Thorough
 */
export const GEMINI_MODELS = {
  flash: {
    id: 'flash',
    name: 'Quick',
    description: 'Fast and efficient video analysis',
    modelId: 'gemini-2.5-flash', // Gemini 2.5 Flash - fast + cost-effective
    speed: 'fastest',
    quality: 'excellent',
    videoCapabilities: [
      'Gemini 2.5 video understanding',
      'Frame-by-frame analysis',
      'Motion detection',
      'Biomechanical inference',
    ],
    limitations: [
      'Lower reasoning depth',
    ],
    recommended: true,
  },
  pro: {
    id: 'pro',
    name: 'Standard',
    description: 'Balanced video analysis with advanced reasoning',
    modelId: 'gemini-3.1-pro-preview', // Gemini 3.1 Pro Preview - recommended default
    speed: 'fast',
    quality: 'excellent',
    videoCapabilities: [
      'Gemini 3.1 Pro video understanding',
      'Detailed motion analysis',
      'Multi-frame comparison',
      'Biomechanical inference',
      'Advanced reasoning',
      'YouTube URL analysis',
    ],
    limitations: [
      'Moderate latency',
    ],
    recommended: false,
  },
  max: {
    id: 'max',
    name: 'Thorough',
    description: 'Full-featured video analysis with deepest reasoning',
    modelId: 'gemini-3.1-pro-preview', // Gemini 3.1 Pro - best reasoning
    speed: 'balanced',
    quality: 'best',
    videoCapabilities: [
      'Native Gemini 3.1 Pro multimodal processing',
      'Up to 10 FPS frame understanding',
      'Pixel-precise object tracking',
      'Advanced motion recognition',
      'Deep causal reasoning',
      'Scene understanding with timestamps',
      'YouTube URL analysis',
    ],
    limitations: [
      'Higher latency for thorough analysis',
    ],
    recommended: false,
  },
};

/**
 * Generate context prompt from uploaded context files
 * @param {Array} contextFiles - Array of context files with name, type, and textContent
 * @returns {string} Context prompt string
 */
function generateContextPrompt(contextFiles) {
  if (!contextFiles || contextFiles.length === 0) return '';

  let contextPrompt = '\n\n## PRE-ANALYSIS CONTEXT DOCUMENTS\n';
  contextPrompt += 'The following reference materials were provided for context. Reference these when relevant to the analysis:\n\n';

  contextFiles.forEach((cf, idx) => {
    contextPrompt += `### Context Document ${idx + 1}: ${cf.name || 'Document'}\n`;
    contextPrompt += `**Type:** ${(cf.type || 'text').toUpperCase()}\n`;
    contextPrompt += '```\n';
    // Limit context to 5000 chars per document to avoid token limits
    const content = cf.textContent?.substring(0, 5000) || cf.content?.substring(0, 5000) || '[Content unavailable]';
    contextPrompt += content;
    if ((cf.textContent || cf.content)?.length > 5000) {
      contextPrompt += '\n[...content truncated for length]';
    }
    contextPrompt += '\n```\n\n';
  });

  contextPrompt += '---\n\n';
  return contextPrompt;
}

/**
 * Get MIME type from file or data URL
 * @param {Object} item - Media item
 * @returns {string} MIME type
 */
function getMimeType(item) {
  if (item.mimeType) return item.mimeType;
  if (item.type) return item.type;
  if (item.file?.type) return item.file.type;

  // Try to extract from base64 data URL
  if (item.base64Data?.startsWith('data:')) {
    const match = item.base64Data.match(/^data:([^;]+);/);
    if (match) return match[1];
  }

  // Default based on common patterns
  return 'application/octet-stream';
}

/**
 * Analyze media using Gemini API with Mixture of Experts (MoE) system
 *
 * @param {Object} options - Analysis options
 * @param {Array} options.mediaItems - Array of media items with base64Data property
 * @param {string} options.analysisMode - The type of analysis: 'pitch-tipping' | 'big-league-hitter' | 'basketball-analysis'
 * @param {Array} [options.contextFiles] - Optional array of context files
 * @param {string} [options.model='pro'] - Gemini model to use ('flash', 'pro', or 'max')
 * @param {string} [options.systemPrompt] - Custom system prompt (overrides MoE if provided)
 * @param {Object} [options.teamConfig] - Team configuration for branding and custom prompts
 * @param {string} [options.userPrompt] - User's custom prompt (HIGHEST PRIORITY - takes precedence over defaults)
 * @returns {Promise<string>} Analysis result text
 */
export const analyzeMedia = async ({
  mediaItems,
  analysisMode = 'pitch-tipping',
  contextFiles = [],
  model = 'pro',
  systemPrompt,
  teamConfig,
  userPrompt
}) => {
  // Validate analysis mode against known modes
  const VALID_MODES = new Set([
    'pitch-tipping', 'simple-mechanics', 'big-league-hitter',
    'basketball-analysis', 'basketball-biomechanics', 'basketball-shooting',
    'basketball-team-defense', 'basketball-game-film',
    'deep-analysis-baseball', 'deep-analysis-basketball',
    'golf-swing', 'golf-biomechanics', 'golf-kinematic-sequence',
    'golf-short-game', 'golf-course-management', 'deep-analysis-golf',
    'soccer-tactical', 'soccer-biomechanics', 'soccer-attacking',
    'soccer-defensive', 'soccer-set-pieces', 'deep-analysis-soccer',
  ]);
  if (analysisMode && !VALID_MODES.has(analysisMode)) {
    const fallbackMode = analysisMode?.includes('basketball') ? 'basketball-analysis'
      : analysisMode?.includes('golf') ? 'golf-swing'
      : analysisMode?.includes('soccer') ? 'soccer-tactical'
      : 'pitch-tipping';
    console.warn(`[analyzeMedia] Unknown analysis mode: ${analysisMode}, defaulting to ${fallbackMode}`);
    analysisMode = fallbackMode;
  }

  // Prepare contents parts
  const parts = [];

  // Inject context files first (as text) if provided
  if (contextFiles && contextFiles.length > 0) {
    const readyContextFiles = contextFiles.filter(cf =>
      (cf.status === 'ready' || !cf.status) && (cf.textContent || cf.content)
    );
    if (readyContextFiles.length > 0) {
      const contextPrompt = generateContextPrompt(readyContextFiles);
      parts.push({ text: contextPrompt });
    }
  }

  // Add media items
  for (const item of mediaItems) {
    // YouTube URLs — pass directly as fileData (Gemini supports YouTube natively)
    if (item.youtubeUrl) {
      const ytUrl = item.youtubeUrl.startsWith('http') ? item.youtubeUrl : `https://${item.youtubeUrl}`;
      parts.push({
        fileData: {
          fileUri: ytUrl,
          mimeType: 'video/mp4',
        },
      });
      parts.push({ text: `[YouTube Video: ${item.name || ytUrl}]` });
      continue;
    }

    if (!item.base64Data) continue;

    const mimeType = getMimeType(item);
    const fileName = item.name || item.fileName || 'media';
    const fileSize = item.file?.size || item.size || 0;

    // Check if this is a CSV file - decode to text for better analysis
    if (item.type === MediaType.CSV || mimeType === 'text/csv' || mimeType.startsWith('text/csv')) {
      // Decode base64 to text for CSVs to give the model better context as text
      try {
        const textContent = atob(stripBase64Prefix(item.base64Data));
        parts.push({
          text: `File: ${fileName} (CSV Content):\n\`\`\`csv\n${textContent}\n\`\`\``
        });
      } catch (e) {
        console.error("Failed to decode CSV", e);
      }
    } else if (item.type === MediaType.VIDEO || mimeType.startsWith('video/') || fileSize > FILE_API_THRESHOLD) {
      // Use backend proxy for videos and large files (avoids referrer restrictions)
      try {
        console.log(`Uploading ${fileName} via backend proxy (${(fileSize / 1024 / 1024).toFixed(1)}MB)...`);

        // For File API, we need the actual file object
        let fileToUpload = item.file;

        // If we only have base64, convert to Blob/File
        if (!fileToUpload && item.base64Data) {
          const base64Content = stripBase64Prefix(item.base64Data);
          const binaryString = atob(base64Content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          fileToUpload = new Blob([bytes], { type: mimeType });
        }

        // Smart upload: uses GCS for large files (>32MB), backend proxy for smaller files
        // This allows uploading videos up to 2GB by bypassing Cloud Run's 32MB limit
        const uploadedFile = await smartUploadFile(fileToUpload, fileName, mimeType);

        if (uploadedFile.uri) {
          // Reference uploaded file - Gemini can read directly from gs:// URIs!
          parts.push({
            fileData: {
              fileUri: uploadedFile.uri,
              mimeType: uploadedFile.mimeType || mimeType,
            },
          });

          // Add a label for the file
          parts.push({
            text: `[Attached Media: ${fileName}]`
          });

          console.log(`Uploaded ${fileName} successfully (uri: ${uploadedFile.uri})`);

          // Store fileUri on the item so post-analysis chat can reference the same video
          item.fileUri = uploadedFile.uri;
        }
      } catch (uploadError) {
        console.error(`Failed to upload ${fileName}:`, uploadError);
        throw new Error(`Failed to upload ${fileName}: ${uploadError.message}`);
      }
    } else {
      // Use inline data for images and small files
      parts.push({
        inlineData: {
          mimeType: mimeType,
          data: stripBase64Prefix(item.base64Data),
        },
      });
      parts.push({
        text: `[Attached Media: ${fileName}]`
      });
    }
  }

  // Add analysis mode as context (informational - does not override user intent)
  if (analysisMode) {
    parts.push({ text: `\n\nAnalysis Mode: ${analysisMode}\n` });
  }

  // USER PROMPT FIRST - Highest priority (user's explicit instructions)
  // Then template/default prompt if no user prompt provided
  if (userPrompt && userPrompt.trim()) {
    // User provided specific instructions - these take priority over any template
    parts.push({ text: `\n**User Instructions (PRIORITY):**\n${userPrompt.trim()}\n\nAnalyze the media following these specific instructions.` });
  } else {
    // No user prompt - use generic analysis request (template defaults handled elsewhere)
    parts.push({ text: '\nPlease analyze the provided media and provide your detailed analysis.' });
  }

  try {
    // Get the model ID from the selected model config
    const modelConfig = GEMINI_MODELS[model] || GEMINI_MODELS.pro;
    const modelId = modelConfig.modelId;

    console.log(`[GeminiService] Using model: ${modelConfig.name} (${modelId})`);
    console.log(`[GeminiService] Analysis mode: ${analysisMode}`);

    // Generate system prompt based on analysis mode (MoE architecture)
    // Basketball modes are routed to basketball-specific prompts.
    // Baseball modes use the existing MoE prompts.
    let finalSystemPrompt;

    if (systemPrompt) {
      // Use custom system prompt if explicitly provided
      finalSystemPrompt = systemPrompt;
    } else if (analysisMode === 'simple-mechanics') {
      // Simple mechanical analysis - fast, focused, single output
      finalSystemPrompt = SIMPLE_MECHANICS_PROMPT;
      console.log('[GeminiService] Using Simple Mechanics analysis (gemini-2.5-flash)');
    } else if (analysisMode.startsWith('basketball-') || analysisMode === 'deep-analysis-basketball') {
      // Basketball modes — each gets a sport-specific system prompt
      finalSystemPrompt = generateBasketballSystemPrompt(analysisMode);
      console.log(`[GeminiService] Using basketball system prompt for mode: ${analysisMode}`);
    } else if (analysisMode.startsWith('golf-') || analysisMode === 'deep-analysis-golf') {
      // Golf modes — each gets a sport-specific system prompt
      finalSystemPrompt = generateGolfSystemPrompt(analysisMode);
      console.log(`[GeminiService] Using golf system prompt for mode: ${analysisMode}`);
    } else if (analysisMode.startsWith('soccer-') || analysisMode === 'deep-analysis-soccer') {
      // Soccer modes — each gets a sport-specific system prompt
      finalSystemPrompt = generateSoccerSystemPrompt(analysisMode);
      console.log(`[GeminiService] Using soccer system prompt for mode: ${analysisMode}`);
    } else if (analysisMode === 'big-league-hitter') {
      // Baseball hitter assessment MoE
      finalSystemPrompt = generateHitterMoESystemPrompt();
      console.log('[GeminiService] Using Big League Hitter Assessment MoE');
    } else if (analysisMode === 'deep-analysis-baseball') {
      // Baseball deep analysis — use MoE prompt (the DEEP_ANALYSIS_SYSTEM_PROMPT
      // is passed as defaultPrompt from analysisModes.js via mode config)
      finalSystemPrompt = generateMoESystemPrompt();
      console.log('[GeminiService] Using Deep Analysis Baseball (MoE)');
    } else {
      // Default fallback: sport-aware (check if mode name suggests basketball or golf)
      if (analysisMode?.includes('basketball') || analysisMode?.includes('shooting') || analysisMode?.includes('defense')) {
        finalSystemPrompt = generateBasketballSystemPrompt('basketball-analysis');
        console.log(`[GeminiService] Using basketball fallback for unknown mode: ${analysisMode}`);
      } else if (analysisMode?.includes('golf') || analysisMode?.includes('swing') || analysisMode?.includes('kinematic')) {
        finalSystemPrompt = generateGolfSystemPrompt('golf-swing');
        console.log(`[GeminiService] Using golf fallback for unknown mode: ${analysisMode}`);
      } else if (analysisMode?.includes('soccer') || analysisMode?.includes('pitch-control') || analysisMode?.includes('pressing')) {
        finalSystemPrompt = generateSoccerSystemPrompt('soccer-tactical');
        console.log(`[GeminiService] Using soccer fallback for unknown mode: ${analysisMode}`);
      } else {
        finalSystemPrompt = generateMoESystemPrompt();
        console.log(`[GeminiService] Using Pitch Tipping Detection MoE (default) for mode: ${analysisMode}`);
      }
    }

    // Append team-specific system prompt additions if configured
    if (teamConfig?.analysis?.systemPromptAdditions) {
      finalSystemPrompt += `\n\n## TEAM-SPECIFIC CONTEXT\n${teamConfig.analysis.systemPromptAdditions}`;
    }

    // Add report branding section — sport-aware
    const isBasketballMode = analysisMode?.startsWith('basketball-') || analysisMode === 'deep-analysis-basketball';
    const isGolfMode = analysisMode?.startsWith('golf-') || analysisMode === 'deep-analysis-golf';
    const isSoccerMode = analysisMode?.startsWith('soccer-') || analysisMode === 'deep-analysis-soccer';
    const platformName = teamConfig?.branding?.name || 'SLAM Sports AI';
    const shortName = teamConfig?.branding?.shortName || 'SLAM';

    if (isBasketballMode) {
      // Basketball branding — use appropriate basketball report type
      const basketballReportTypes = {
        'basketball-analysis': 'Basketball Analysis',
        'basketball-biomechanics': 'Biomechanics Analysis',
        'basketball-shooting': 'Shooting Mechanics Analysis',
        'basketball-team-defense': 'Team Defense Analysis',
        'basketball-game-film': 'Full Game Analysis',
        'deep-analysis-basketball': 'Deep Basketball Analysis',
      };
      const reportType = basketballReportTypes[analysisMode] || 'Basketball Analysis';

      finalSystemPrompt += `\n\n## REPORT BRANDING
- Platform Name: ${platformName}
- Analysis Type: ${reportType}
- Report Title Format: "${shortName} ${reportType}"
- Generated By: ${platformName}
`;
    } else if (isGolfMode) {
      // Golf branding — use appropriate golf report type
      const golfReportTypes = {
        'golf-swing': 'Swing Analysis',
        'golf-biomechanics': 'Biomechanics Analysis',
        'golf-kinematic-sequence': 'Kinematic Sequence Analysis',
        'golf-short-game': 'Short Game Analysis',
        'golf-course-management': 'Course Management Analysis',
        'deep-analysis-golf': 'Deep Golf Analysis',
      };
      const reportType = golfReportTypes[analysisMode] || 'Swing Analysis';

      finalSystemPrompt += `\n\n## REPORT BRANDING
- Platform Name: ${platformName}
- Analysis Type: ${reportType}
- Report Title Format: "${shortName} ${reportType}"
- Generated By: ${platformName}
`;
    } else if (isSoccerMode) {
      // Soccer branding — use appropriate soccer report type
      const soccerReportTypes = {
        'soccer-tactical': 'Tactical Analysis',
        'soccer-biomechanics': 'Biomechanics Analysis',
        'soccer-attacking': 'Attacking Patterns Analysis',
        'soccer-defensive': 'Defensive Organization Analysis',
        'soccer-set-pieces': 'Set Piece Analysis',
        'deep-analysis-soccer': 'Deep Soccer Analysis',
      };
      const reportType = soccerReportTypes[analysisMode] || 'Tactical Analysis';

      finalSystemPrompt += `\n\n## REPORT BRANDING
- Platform Name: ${platformName}
- Analysis Type: ${reportType}
- Report Title Format: "${shortName} ${reportType}"
- Generated By: ${platformName}
`;
    } else {
      // Baseball branding — existing logic
      let reportType = 'Advanced Scouting';
      if (analysisMode === 'big-league-hitter') reportType = 'Big League Hitter Assessment';
      if (analysisMode === 'deep-analysis-baseball') reportType = 'Deep Baseball Analysis';
      if (analysisMode === 'simple-mechanics') reportType = 'Quick Mechanics Analysis';

      finalSystemPrompt += `\n\n## REPORT BRANDING
- Platform Name: ${platformName}
- Analysis Type: ${reportType}
- Report Title Format: "${shortName} ${reportType}"
- Generated By: ${platformName}
`;
    }

    // Use backend proxy to avoid HTTP referrer restrictions on API key
    // This routes the request through Cloud Run where the API key is configured
    const result = await generateContentViaProxy(parts, modelId, finalSystemPrompt, 0.3);

    return result || "No response generated.";
  } catch (error) {
    console.error("Gemini API Error:", error);
    throw error;
  }
};

/**
 * Get available Gemini models
 * @returns {Array} Array of model configurations
 */
export function getAvailableModels() {
  return Object.values(GEMINI_MODELS);
}

/**
 * Get model by ID
 * @param {string} modelId - Model ID ('flash' or 'pro')
 * @returns {Object|undefined} Model configuration
 */
export function getModelById(modelId) {
  return GEMINI_MODELS[modelId];
}

/**
 * Quick player evaluation using Gemini 3 Flash
 * Returns a short technical baseball analysis
 *
 * @param {Object} options - Evaluation options
 * @param {Object} options.player - Player data
 * @param {string} options.sport - Sport code (e.g., 'CBB', 'MLB')
 * @returns {Promise<string>} Short evaluation text
 */
export const evaluatePlayer = async ({ player, sport = 'CBB' }) => {
  // Build player context
  const playerContext = {
    name: player.name,
    position: player.position || 'Unknown',
    height: player.height || 'Unknown',
    weight: player.weight || 'Unknown',
    team: player.team || player.teamName || 'Unknown',
    stats: player.stats || player.seasonStats || {},
    highlights: player.highlights || [],
    rating: player.rating || player.slamScore || null,
  };

  // Create a focused prompt for quick evaluation
  const prompt = `You are a professional ${sport === 'MLB' || sport === 'MiLB' ? 'MLB' : 'college'} baseball scout.
Provide a BRIEF (2-3 sentences max) technical evaluation of this player focusing on:
- Their strengths and weaknesses
- Key areas for development
- One specific technical recommendation

Keep it CONCISE and ACTIONABLE - like a scout's quick note.

Player: ${playerContext.name}
Position: ${playerContext.position}
Physical: ${playerContext.height}, ${playerContext.weight}
Team: ${playerContext.team}
${playerContext.rating ? `Rating: ${playerContext.rating}` : ''}
${Object.keys(playerContext.stats).length > 0 ? `Stats: ${JSON.stringify(playerContext.stats)}` : ''}

Your evaluation:`;

  const parts = [{ text: prompt }];
  const systemPrompt = 'You are a professional baseball scout providing quick, actionable evaluations.';

  try {
    // Use backend proxy to avoid HTTP referrer restrictions
    const result = await generateContentViaProxy(parts, 'gemini-3-flash-preview', systemPrompt, 0.4);
    return result || "Unable to generate evaluation.";
  } catch (error) {
    console.error("Gemini API Error (evaluatePlayer):", error);
    throw error;
  }
};

/**
 * Expert Drill-Down Analysis
 * Re-analyze media with a focused prompt for a specific expert area
 *
 * @param {Object} options - Analysis options
 * @param {Array} options.mediaItems - Media items to analyze
 * @param {string} options.expertName - Expert persona name (e.g., 'BIOMECHANICS_EXPERT')
 * @param {Array} [options.contextFiles] - Optional context files
 * @returns {Promise<string>} Detailed expert analysis
 */
export const drillDownAnalysis = async ({
  mediaItems,
  expertName,
  contextFiles = [],
}) => {
  const drillDownPrompt = EXPERT_DRILLDOWN_PROMPTS[expertName];

  if (!drillDownPrompt) {
    throw new Error(`Unknown expert: ${expertName}. Valid experts: ${Object.keys(EXPERT_DRILLDOWN_PROMPTS).join(', ')}`);
  }

  console.log(`[GeminiService] Drill-down analysis for ${expertName}`);

  // Use analyzeMedia with the drill-down prompt as systemPrompt
  // Use flash model for faster response
  return analyzeMedia({
    mediaItems,
    analysisMode: 'simple-mechanics', // Use simple mode flag
    contextFiles,
    model: 'flash', // Always use flash for drill-down (faster)
    systemPrompt: drillDownPrompt,
    userPrompt: `Provide a detailed ${expertName.replace(/_/g, ' ')} analysis of this pitcher.`,
  });
};

/**
 * Get available expert names for drill-down
 * @returns {Array<string>} List of expert names
 */
export const getAvailableExperts = () => {
  return Object.keys(EXPERT_DRILLDOWN_PROMPTS);
};

/**
 * Get expert display info
 * @param {string} expertName - Expert persona name
 * @returns {Object} Expert display info
 */
export const getExpertInfo = (expertName) => {
  const expert = EXPERT_PERSONAS?.find(e => e.name === expertName);
  if (!expert) return null;

  return {
    name: expertName,
    displayName: expert.role,
    expertise: expert.expertise,
    focusAreas: expert.focusAreas,
  };
};

export default {
  analyzeMedia,
  evaluatePlayer,
  drillDownAnalysis,
  getAvailableModels,
  getModelById,
  getAvailableExperts,
  getExpertInfo,
  GEMINI_MODELS
};
